import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  ArrowLeft, Loader2, BookOpen, AlertCircle, ChevronLeft, ChevronRight,
  ZoomIn, ZoomOut, Maximize, Flag, Search, X, Highlighter, StickyNote,
  Bookmark, Sun, Moon, BookOpenCheck, File, FileText, Volume2, Play, Pause, Square,
  Gauge, SkipBack, SkipForward, Quote, Copy, Check, Heart, Link2, Menu
} from 'lucide-react';
import axios from 'axios';
import { Bot } from 'lucide-react';
import { BookAIChat, AI_GOLD } from './BookAIChat';
import { ReaderControlsMenu } from './ReaderControlsMenu';
import { ReportContentModal } from './ReportContentModal';
import { reloadIfSuspended } from '../lib/editorRef';

const COMPACT_QUERY = '(max-width: 1024px)';
const PORTRAIT_QUERY = '(orientation: portrait)';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Highlight colors
const HIGHLIGHT_COLORS = [
  { name: 'Amarelo', value: '#FFFF00' },
  { name: 'Verde', value: '#90EE90' },
  { name: 'Azul', value: '#87CEEB' },
  { name: 'Rosa', value: '#FFB6C1' },
  { name: 'Laranja', value: '#FFA500' },
];

// Copy Button Component with feedback
const isIssn = (code) => /^\d{4}-?\d{3}[\dXx]$/.test(String(code || '').trim());
const identifierLabel = (code) => (code ? `${isIssn(code) ? 'ISSN' : 'ISBN'} ${String(code).trim()}` : '');
const buildCitation = (text, book, page) => `"${text}" — ${[
  book?.titulo || 'Livro Digital', book?.autor, `p. ${page}`, identifierLabel(book?.isbn)
].filter(Boolean).join(', ')}`;

const CopyButton = ({ text, book, page, isDarkMode }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    const citation = buildCitation(text, book, page);
    try {
      await navigator.clipboard.writeText(citation);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = citation;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  return (
    <Button 
      onClick={handleCopy}
      data-testid="cite-copy-btn"
      style={{ 
        background: copied 
          ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
          : 'linear-gradient(135deg, #68AAB0 0%, #5a9a9f 100%)',
        color: 'white',
        padding: '0.75rem 1.5rem',
        fontSize: '1rem',
        fontWeight: 600,
        borderRadius: '8px',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        transition: 'all 0.2s ease'
      }}
    >
      {copied ? (
        <>
          <Check className="w-5 h-5" />
          Copiado!
        </>
      ) : (
        <>
          <Copy className="w-5 h-5" />
          Copiar Citação
        </>
      )}
    </Button>
  );
};

// Lowercase + strip accents, keeping string length so positions map 1:1
const foldText = (text) => Array.from(text.toLowerCase(), (c) => {
  const f = c.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return f.length === 1 ? f : c;
}).join('');

// Rebuild page text from PDF.js items using positions (spaces between words, line breaks, hyphenation)
const buildPageText = (items) => {
  let out = '';
  let prev = null;
  for (const item of items) {
    if (!item.str) { if (item.hasEOL) out += ' '; continue; }
    if (prev) {
      const sameLine = Math.abs(item.transform[5] - prev.transform[5]) < Math.max(2, Math.abs(prev.transform[3]) * 0.5);
      if (!sameLine) {
        if (/[A-Za-zÀ-ÿ]-$/.test(out)) out = out.slice(0, -1);
        else out += ' ';
      } else {
        const gap = item.transform[4] - (prev.transform[4] + prev.width);
        if (gap > Math.abs(prev.transform[0] || prev.transform[3] || 10) * 0.15 && !/\s$/.test(out) && !/^\s/.test(item.str)) out += ' ';
      }
    }
    out += item.str;
    if (item.hasEOL) out += ' ';
    prev = item;
  }
  return out.replace(/([A-Za-zÀ-ÿ])-\s+(?=[A-Za-zÀ-ÿ])/g, '$1').replace(/\s+/g, ' ').trim();
};

let lastTouchAt = 0;
if (typeof window !== 'undefined') {
  window.addEventListener('touchstart', () => { lastTouchAt = Date.now(); }, { capture: true, passive: true });
}
const isRecentTouch = () => Date.now() - lastTouchAt < 2000;
const inPdfTextLayer = (target) => Boolean(target && target.closest && target.closest('.react-pdf__Page__textContent'));

const compactIconBtn = {
  width: '44px', height: '44px', flexShrink: 0, borderRadius: '10px', border: 'none', background: 'transparent',
  color: '#cbd5e0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
};

const BookViewer = ({ editorId, editorSlug }) => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const pagesContainerRef = useRef(null);

  // Memoize PDF options to prevent Document from reloading on every render
  // (React-PDF treats a new object reference as a change -> aborts in-flight fetch -> transport destroyed)
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);

  // Memoize error handler to prevent Document reload on re-render
  const handlePdfLoadError = useCallback((error) => {
    console.error('PDF Load Error:', error);
    // Don't show error for aborted requests (these happen in StrictMode double-render)
    if (error.message && !error.message.includes('aborted')) {
      setError('Erro ao carregar o PDF. Tente recarregar a página.');
    }
  }, []);

  // Memoize loading and error components
  const PdfLoadingComponent = useMemo(() => (
    <div style={{ 
      width: '1000px', 
      height: '700px', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'white'
    }}>
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#14b8a6' }} />
    </div>
  ), []);

  const PdfErrorComponent = useMemo(() => (
    <div style={{ 
      width: '500px', 
      height: '700px', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      flexDirection: 'column',
      background: 'white'
    }}>
      <AlertCircle className="w-12 h-12 mb-4" style={{ color: '#ef4444' }} />
      <p>Erro ao carregar PDF</p>
      <Button 
        onClick={() => window.location.reload()}
        style={{ marginTop: '1rem' }}
      >
        Recarregar
      </Button>
    </div>
  ), []);

  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [book, setBook] = useState(null);
  const [editorName, setEditorName] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [showControlsMenu, setShowControlsMenu] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);
  const [compactHeaderH, setCompactHeaderH] = useState(52);
  const compactHeaderRef = useCallback((el) => { if (el) setCompactHeaderH(el.offsetHeight); }, []);
  const [isCompact, setIsCompact] = useState(() => window.matchMedia(COMPACT_QUERY).matches);
  const [isPortrait, setIsPortrait] = useState(() => window.matchMedia(PORTRAIT_QUERY).matches);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSearchSidebar, setShowSearchSidebar] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [textIndexProgress, setTextIndexProgress] = useState(0);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pdfTextByPage, setPdfTextByPage] = useState({});
  const [showSidebar, setShowSidebar] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [selectedColor, setSelectedColor] = useState('#FFFF00');
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [notePage, setNotePage] = useState(0);
  const [viewMode, setViewMode] = useState(() => (window.matchMedia(COMPACT_QUERY).matches && window.matchMedia(PORTRAIT_QUERY).matches ? 'single' : 'double'));

  useEffect(() => {
    const cq = window.matchMedia(COMPACT_QUERY);
    const pq = window.matchMedia(PORTRAIT_QUERY);
    const sync = () => { setIsCompact(cq.matches); setIsPortrait(pq.matches); };
    cq.addEventListener('change', sync);
    pq.addEventListener('change', sync);
    return () => { cq.removeEventListener('change', sync); pq.removeEventListener('change', sync); };
  }, []);

  useEffect(() => {
    if (isCompact) setViewMode(isPortrait ? 'single' : 'double');
  }, [isCompact, isPortrait]);
  const [selectedText, setSelectedText] = useState('');
  const [selectionPosition, setSelectionPosition] = useState(null);
  const [showHighlightPopup, setShowHighlightPopup] = useState(false);
  const [focusedAnnotationId, setFocusedAnnotationId] = useState(null);
  const [sliderValue, setSliderValue] = useState(1);
  const [showSliderTooltip, setShowSliderTooltip] = useState(false);
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [showCopyrightModal, setShowCopyrightModal] = useState(false);
  const [contentProtected, setContentProtected] = useState(false);
  const [showCiteModal, setShowCiteModal] = useState(false);
  const [citeText, setCiteText] = useState('');
  const [citePage, setCitePage] = useState(1);
  const sliderRef = useRef(null);
  
  const [linkCopied, setLinkCopied] = useState(false);
  const highlightPopupRef = useRef(null);
  const [popupPos, setPopupPos] = useState(null);
  const copyBookLink = async () => {
    const url = `${window.location.origin}/${editorSlug}/livro/${bookId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Favorites state
  const [isFavorite, setIsFavorite] = useState(false);
  const [loadingFavorite, setLoadingFavorite] = useState(false);
  
  // Text-to-Speech states
  const [isTTSActive, setIsTTSActive] = useState(false);
  const [isTTSPlaying, setIsTTSPlaying] = useState(false);
  const [ttsRate, setTtsRate] = useState(2);
  const [showTTSControls, setShowTTSControls] = useState(false);
  const [ttsVoices, setTtsVoices] = useState([]);
  const [ttsLines, setTtsLines] = useState([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [currentLineWords, setCurrentLineWords] = useState([]);
  const [ttsPaused, setTtsPaused] = useState(false);
  const utteranceRef = useRef(null);
  const speakLineRef = useRef(null);
  const utteranceIdRef = useRef(0); // Unique ID for each utterance to ignore old callbacks
  const ttsUserPausedRef = useRef(false); // Only true when user explicitly pauses
  
  const token = localStorage.getItem('subscriberToken');

  useEffect(() => {
    if (!token || !bookId) return;
    axios.get(`${API}/digital/books/${bookId}/ai-status?auth=${token}`)
      .then(({ data }) => setAiEnabled(data.enabled))
      .catch(() => setAiEnabled(false));
  }, [bookId, token]);
  
  // Decode subscriber info from token for watermark
  const subscriberInfo = useMemo(() => {
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Format CPF: 123.456.789-00
      const rawCpf = payload.cpf || '';
      const formattedCpf = rawCpf.length === 11 
        ? `${rawCpf.slice(0,3)}.${rawCpf.slice(3,6)}.${rawCpf.slice(6,9)}-${rawCpf.slice(9)}`
        : rawCpf;
      return {
        email: payload.email || '',
        name: payload.name || '',
        cpf: formattedCpf
      };
    } catch (e) {
      return null;
    }
  }, [token]);

  // Function to protect content (dim to 5% visibility)
  const protectContent = useCallback(() => {
    setContentProtected(true);
    setShowCopyrightModal(true);
  }, []);

  // Function to restore content
  const restoreContent = useCallback(() => {
    setContentProtected(false);
  }, []);

  // Text-to-Speech Functions
  const getVisiblePagesText = useCallback(() => {
    // Calculate visible pages directly to avoid dependency on visiblePages
    let pages;
    if (viewMode === 'single') {
      pages = [currentPage];
    } else {
      const spreadIndex = Math.floor((currentPage - 1) / 2);
      const leftPage = spreadIndex * 2 + 1;
      const rightPage = leftPage + 1;
      pages = [leftPage];
      if (rightPage <= numPages) {
        pages.push(rightPage);
      }
    }
    
    let text = '';
    pages.forEach(pageNum => {
      const pageText = pdfTextByPage[pageNum];
      if (pageText) {
        text += pageText + ' ';
      }
    });
    return text.trim();
  }, [viewMode, currentPage, numPages, pdfTextByPage]);

  // Split text into lines (sentences or chunks)
  const getTextLines = useCallback(() => {
    const text = getVisiblePagesText();
    if (!text) return [];
    
    // Split by sentences (period, question mark, exclamation) or by chunks of ~100 chars
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    return sentences.map(s => s.trim()).filter(s => s.length > 0);
  }, [getVisiblePagesText]);

  // Speak a specific line
  const speakLine = useCallback((lineIndex, lines = null) => {
    if (!('speechSynthesis' in window)) {
      alert('Seu navegador não suporta leitura em voz alta.');
      return;
    }

    const allLines = lines || (ttsLines.length > 0 ? ttsLines : getTextLines());
    if (allLines.length === 0) {
      alert('Não há texto para ler nesta página.');
      return;
    }

    // Store lines if not already stored
    if (ttsLines.length === 0 && !lines) {
      setTtsLines(allLines);
    }

    const safeIndex = Math.max(0, Math.min(lineIndex, allLines.length - 1));
    const text = allLines[safeIndex];
    
    if (!text) return;

    // Generate unique ID for this utterance
    const currentUtteranceId = ++utteranceIdRef.current;

    // Split text into words for highlighting
    const words = text.split(/\s+/).filter(w => w.length > 0);
    setCurrentLineWords(words);
    setCurrentWordIndex(-1);

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();
    
    // Reset pause flag when starting new speech
    ttsUserPausedRef.current = false;

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'pt-BR';
    utterance.rate = ttsRate;
    utterance.pitch = 0.95; // Slightly lower for more natural male voice
    utterance.volume = 1.0;

    // Find the best MALE voice available
    // Priority: Male Google/Microsoft voices > Other male voices > Any Portuguese voice
    const voices = ttsVoices.length > 0 ? ttsVoices : window.speechSynthesis.getVoices();
    
    const findBestMaleVoice = () => {
      const ptVoices = voices.filter(v => v.lang.startsWith('pt'));
      
      // Common male voice name patterns
      const maleNames = ['antonio', 'daniel', 'ricardo', 'paulo', 'pedro', 'male', 'masculin'];
      const femaleNames = ['francisca', 'maria', 'ana', 'female', 'femin', 'luciana', 'vitoria'];
      
      const isMaleVoice = (voice) => {
        const name = voice.name.toLowerCase();
        return maleNames.some(m => name.includes(m)) || !femaleNames.some(f => name.includes(f));
      };
      
      // Priority 1: Microsoft Antonio (high quality male voice)
      const antonioVoice = ptVoices.find(v => 
        v.name.toLowerCase().includes('antonio')
      );
      if (antonioVoice) return antonioVoice;
      
      // Priority 2: Any named male voice from Microsoft
      const msMaleVoice = ptVoices.find(v => 
        v.name.toLowerCase().includes('microsoft') && isMaleVoice(v)
      );
      if (msMaleVoice) return msMaleVoice;
      
      // Priority 3: Google Portuguese voice (adjust pitch for male tone)
      const googleVoice = ptVoices.find(v => 
        v.name.toLowerCase().includes('google') || 
        v.voiceURI.toLowerCase().includes('google')
      );
      if (googleVoice) return googleVoice;
      
      // Priority 4: Any online male voice
      const onlineMaleVoice = ptVoices.find(v => !v.localService && isMaleVoice(v));
      if (onlineMaleVoice) return onlineMaleVoice;
      
      // Priority 5: Any male Portuguese voice
      const anyMaleVoice = ptVoices.find(v => isMaleVoice(v));
      if (anyMaleVoice) return anyMaleVoice;
      
      // Fallback: first Portuguese voice
      if (ptVoices.length > 0) return ptVoices[0];
      
      return voices[0];
    };
    
    const selectedVoice = findBestMaleVoice();
    if (selectedVoice) {
      utterance.voice = selectedVoice;
      console.log('Using voice:', selectedVoice.name, '| Online:', !selectedVoice.localService);
    }

    utterance.onstart = () => {
      console.log('TTS started line', safeIndex + 1, 'of', allLines.length);
      setIsTTSPlaying(true);
      setIsTTSActive(true);
      setCurrentLineIndex(safeIndex);
      setCurrentWordIndex(0);
      setTtsPaused(false);
    };

    // Track word boundaries for highlighting
    utterance.onboundary = (event) => {
      if (event.name === 'word') {
        const charIndex = event.charIndex;
        let currentPos = 0;
        
        for (let i = 0; i < words.length; i++) {
          const wordStart = text.indexOf(words[i], currentPos);
          const wordEnd = wordStart + words[i].length;
          
          if (charIndex >= wordStart && charIndex < wordEnd) {
            setCurrentWordIndex(i);
            break;
          }
          currentPos = wordEnd;
        }
      }
    };

    utterance.onend = () => {
      console.log('TTS ended line', safeIndex + 1, 'utteranceId:', currentUtteranceId, 'current:', utteranceIdRef.current);
      setCurrentWordIndex(-1);
      
      // Ignore this callback if a newer utterance was started (manual navigation or rate change)
      if (currentUtteranceId !== utteranceIdRef.current) {
        console.log('Ignoring old utterance callback');
        return;
      }
      
      // Check if user explicitly paused
      if (ttsUserPausedRef.current) {
        console.log('User paused, not advancing');
        return;
      }
      
      // Auto-advance to next line if there are more lines
      const nextIndex = safeIndex + 1;
      if (nextIndex < allLines.length) {
        console.log('Auto-advancing to line', nextIndex + 1);
        setTimeout(() => {
          // Double check pause state before advancing
          if (speakLineRef.current && !ttsUserPausedRef.current) {
            speakLineRef.current(nextIndex, allLines);
          }
        }, 200);
      } else {
        console.log('TTS finished all lines');
        setIsTTSPlaying(false);
        setIsTTSActive(false);
      }
    };

    utterance.onerror = (event) => {
      console.error('TTS error:', event.error);
      // Don't stop everything on error, just log it
      if (event.error !== 'interrupted' && event.error !== 'canceled') {
        setIsTTSPlaying(false);
        setCurrentWordIndex(-1);
      }
    };

    utteranceRef.current = utterance;
    
    setIsTTSPlaying(true);
    setIsTTSActive(true);
    setCurrentLineIndex(safeIndex);
    ttsJustStartedRef.current = true;
    
    window.speechSynthesis.speak(utterance);
  }, [ttsLines, getTextLines, ttsRate, ttsVoices]);

  // Keep ref updated with latest speakLine function
  useEffect(() => {
    speakLineRef.current = speakLine;
  }, [speakLine]);

  const startTTS = useCallback(() => {
    const lines = getTextLines();
    setTtsLines(lines);
    setCurrentLineIndex(0);
    setTtsPaused(false);
    ttsUserPausedRef.current = false;
    speakLine(0, lines);
  }, [getTextLines, speakLine]);

  const pauseTTS = useCallback(() => {
    if (window.speechSynthesis.speaking) {
      ttsUserPausedRef.current = true; // Mark as paused by user
      window.speechSynthesis.cancel();
      setIsTTSPlaying(false);
      setTtsPaused(true);
    }
  }, []);

  const resumeTTS = useCallback(() => {
    if (ttsPaused && ttsLines.length > 0) {
      setTtsPaused(false);
      ttsUserPausedRef.current = false;
      speakLine(currentLineIndex, ttsLines);
    }
  }, [ttsPaused, ttsLines, currentLineIndex, speakLine]);

  // Stop and rewind: the next start reads from the top of the current page
  const resetTTS = useCallback(() => {
    ttsUserPausedRef.current = true;
    window.speechSynthesis.cancel();
    setIsTTSPlaying(false);
    setIsTTSActive(false);
    setTtsLines([]);
    setCurrentLineIndex(0);
    setCurrentWordIndex(-1);
    setCurrentLineWords([]);
    setTtsPaused(false);
    utteranceIdRef.current = 0;
  }, []);

  const stopTTS = useCallback(() => {
    ttsUserPausedRef.current = true;
    window.speechSynthesis.cancel();
    setIsTTSPlaying(false);
    setIsTTSActive(false);
    setShowTTSControls(false);
    setTtsLines([]);
    setCurrentLineIndex(0);
    setCurrentWordIndex(-1);
    setCurrentLineWords([]);
    setTtsPaused(false);
    utteranceIdRef.current = 0;
  }, []);

  const toggleTTSPlayPause = useCallback(() => {
    if (!isTTSActive) {
      startTTS();
    } else if (isTTSPlaying) {
      pauseTTS();
    } else {
      resumeTTS();
    }
  }, [isTTSActive, isTTSPlaying, startTTS, pauseTTS, resumeTTS]);

  // Continue reading from current word with a new rate
  const continueFromCurrentWord = useCallback((newRate) => {
    if (!isTTSActive || currentLineWords.length === 0) return;
    
    // Get remaining words from current position
    const remainingWords = currentLineWords.slice(Math.max(0, currentWordIndex));
    if (remainingWords.length === 0) return;
    
    const remainingText = remainingWords.join(' ');
    
    // Generate unique ID for this utterance
    const currentUtteranceId = ++utteranceIdRef.current;
    
    // Cancel current speech
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(remainingText);
    utterance.lang = 'pt-BR';
    utterance.rate = newRate;
    utterance.pitch = 0.95; // Slightly lower for male voice
    utterance.volume = 1.0;
    
    // Find best male voice
    const voices = ttsVoices.length > 0 ? ttsVoices : window.speechSynthesis.getVoices();
    const ptVoices = voices.filter(v => v.lang.startsWith('pt'));
    const maleNames = ['antonio', 'daniel', 'ricardo', 'paulo', 'pedro', 'male'];
    const femaleNames = ['francisca', 'maria', 'ana', 'female', 'luciana', 'vitoria'];
    const isMale = (v) => {
      const n = v.name.toLowerCase();
      return maleNames.some(m => n.includes(m)) || !femaleNames.some(f => n.includes(f));
    };
    
    const bestVoice = ptVoices.find(v => v.name.toLowerCase().includes('antonio'))
      || ptVoices.find(v => v.name.toLowerCase().includes('microsoft') && isMale(v))
      || ptVoices.find(v => v.name.toLowerCase().includes('google'))
      || ptVoices.find(v => !v.localService && isMale(v))
      || ptVoices.find(v => isMale(v))
      || ptVoices[0]
      || voices[0];
    if (bestVoice) {
      utterance.voice = bestVoice;
    }
    
    // Track the offset for word index calculation
    const wordOffset = Math.max(0, currentWordIndex);
    
    utterance.onstart = () => {
      setIsTTSPlaying(true);
      ttsUserPausedRef.current = false;
    };
    
    utterance.onboundary = (event) => {
      if (event.name === 'word') {
        const charIndex = event.charIndex;
        let currentPos = 0;
        
        for (let i = 0; i < remainingWords.length; i++) {
          const wordStart = remainingText.indexOf(remainingWords[i], currentPos);
          const wordEnd = wordStart + remainingWords[i].length;
          
          if (charIndex >= wordStart && charIndex < wordEnd) {
            setCurrentWordIndex(wordOffset + i);
            break;
          }
          currentPos = wordEnd;
        }
      }
    };
    
    utterance.onend = () => {
      setCurrentWordIndex(-1);
      
      // Ignore old callbacks
      if (currentUtteranceId !== utteranceIdRef.current) {
        return;
      }
      
      // Check if user paused
      if (ttsUserPausedRef.current) {
        return;
      }
      
      // Auto-advance to next line
      const nextIndex = currentLineIndex + 1;
      if (nextIndex < ttsLines.length) {
        setTimeout(() => {
          if (speakLineRef.current && !ttsUserPausedRef.current) {
            speakLineRef.current(nextIndex, ttsLines);
          }
        }, 200);
      } else {
        setIsTTSPlaying(false);
        setIsTTSActive(false);
      }
    };
    
    utterance.onerror = (event) => {
      console.error('TTS error:', event.error);
      if (event.error !== 'interrupted' && event.error !== 'canceled') {
        setIsTTSPlaying(false);
      }
    };
    
    utteranceRef.current = utterance;
    setIsTTSPlaying(true);
    
    window.speechSynthesis.speak(utterance);
  }, [isTTSActive, currentLineWords, currentWordIndex, ttsVoices, currentLineIndex, ttsLines]);

  const changeTTSRate = useCallback((newRate) => {
    setTtsRate(newRate);
    // If currently playing, continue from current word with new rate
    if (isTTSPlaying && currentWordIndex >= 0) {
      continueFromCurrentWord(newRate);
    }
  }, [isTTSPlaying, currentWordIndex, continueFromCurrentWord]);

  // Go to previous line - just start the new line, utteranceId handles ignoring old callbacks
  const previousLine = useCallback(() => {
    if (currentLineIndex > 0) {
      speakLine(currentLineIndex - 1, ttsLines);
    }
  }, [currentLineIndex, speakLine, ttsLines]);

  // Go to next line - just start the new line, utteranceId handles ignoring old callbacks
  const nextLine = useCallback(() => {
    if (currentLineIndex < ttsLines.length - 1) {
      speakLine(currentLineIndex + 1, ttsLines);
    }
  }, [currentLineIndex, ttsLines, speakLine]);

  // Cleanup TTS on unmount or page change
  useEffect(() => {
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Track previous page for TTS stop on page change
  const prevPageRef = useRef(null);
  const ttsJustStartedRef = useRef(false);

  // Stop TTS when page changes (not when TTS state changes)
  useEffect(() => {
    // Skip the first render and when TTS just started
    if (prevPageRef.current !== null && prevPageRef.current !== currentPage && isTTSActive && !ttsJustStartedRef.current) {
      ttsUserPausedRef.current = true;
      window.speechSynthesis.cancel();
      setIsTTSPlaying(false);
      setIsTTSActive(false);
      setShowTTSControls(false);
      setTtsLines([]);
      setCurrentLineIndex(0);
      setCurrentWordIndex(-1);
      setCurrentLineWords([]);
      utteranceIdRef.current = 0;
    }
    prevPageRef.current = currentPage;
    ttsJustStartedRef.current = false;
  }, [currentPage, isTTSActive]);

  // Load voices when available
  useEffect(() => {
    if ('speechSynthesis' in window) {
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          setTtsVoices(voices);
          console.log('TTS voices loaded:', voices.length);
        }
      };
      
      // Try to load immediately
      loadVoices();
      
      // Also listen for voiceschanged event (needed for Chrome)
      window.speechSynthesis.onvoiceschanged = loadVoices;
      
      return () => {
        window.speechSynthesis.onvoiceschanged = null;
      };
    }
  }, []);

  // Block screenshot and screen capture attempts
  useEffect(() => {
    // Track if Windows/Meta key is pressed
    let winKeyPressed = false;
    
    // Block PrintScreen key and screenshot shortcuts
    const handleKeyDown = (e) => {
      // Track Windows/Meta key
      if (e.key === 'Meta' || e.key === 'OS' || e.keyCode === 91 || e.keyCode === 92) {
        winKeyPressed = true;
      }
      
      // PrintScreen key
      if (e.key === 'PrintScreen' || e.keyCode === 44) {
        e.preventDefault();
        protectContent();
        return false;
      }
      
      // Windows: Win+Shift+S (Snipping Tool) - detect Shift+S when Win key might be pressed
      // The Windows key is hard to detect, so we catch Shift+S more aggressively
      if (e.shiftKey && (e.key === 's' || e.key === 'S')) {
        // Check multiple ways to detect Windows key
        const hasWinKey = winKeyPressed || 
                          e.metaKey || 
                          (e.getModifierState && e.getModifierState('Meta')) ||
                          (e.getModifierState && e.getModifierState('OS'));
        
        if (hasWinKey) {
          e.preventDefault();
          e.stopPropagation();
          protectContent();
          return false;
        }
      }
      
      // Also block just Shift+S+any other modifier (catches more cases)
      if (e.shiftKey && (e.key === 's' || e.key === 'S') && (e.metaKey || e.ctrlKey || e.altKey)) {
        e.preventDefault();
        e.stopPropagation();
        protectContent();
        return false;
      }
      
      // Mac: Cmd+Shift+3, Cmd+Shift+4, Cmd+Shift+5
      if (
        (e.metaKey && e.shiftKey && ['3', '4', '5'].includes(e.key)) ||
        (e.metaKey && e.key === 'PrintScreen') ||
        (e.ctrlKey && e.key === 'PrintScreen') ||
        (e.altKey && e.key === 'PrintScreen')
      ) {
        e.preventDefault();
        protectContent();
        return false;
      }
      
      // Block Ctrl+P (print)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        protectContent();
        return false;
      }
    };
    
    const handleKeyUp = (e) => {
      // Reset Windows key tracking
      if (e.key === 'Meta' || e.key === 'OS' || e.keyCode === 91 || e.keyCode === 92) {
        winKeyPressed = false;
      }
    };

    // Block right-click context menu
    const handleContextMenu = (e) => {
      // Long-press on the book text is how phones start a selection (highlight/cite)
      if (isRecentTouch() && inPdfTextLayer(e.target)) return;
      e.preventDefault();
      protectContent();
      return false;
    };

    // Block copy (except when citation popup is active)
    const handleCopy = (e) => {
      if (!showHighlightPopup) { // Allow copy only when selection popup is open
        e.preventDefault();
        protectContent();
        return false;
      }
    };

    // Detect when window loses focus - critical for Snipping Tool detection
    // When user opens Snipping Tool (Win+Shift+S or from Start), the browser loses focus
    const handleWindowBlur = () => {
      // Immediate protection when window loses focus
      // This catches the Snipping Tool which steals focus immediately
      protectContent();
    };

    // Detect visibility change (tab switch, minimize)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        protectContent();
      }
    };

    // Detect when window regains focus
    const handleWindowFocus = () => {
      // Keep content protected until user clicks "Entendi"
      // This ensures screenshot capture will only get the protected view
    };

    // Add event listeners - capture phase for earlier interception
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    // CSS to prevent selection on PDF pages (except when highlighting)
    const style = document.createElement('style');
    style.id = 'copyright-protection-style';
    style.textContent = `
      .react-pdf__Page canvas {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
        pointer-events: none !important;
      }
      @media print {
        body * {
          display: none !important;
        }
        body::after {
          content: "Impressão não permitida - Lei de Direitos Autorais";
          display: block !important;
          font-size: 24px;
          text-align: center;
          padding: 50px;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      const existingStyle = document.getElementById('copyright-protection-style');
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [showHighlightPopup, protectContent]);

  // Get annotations for a specific page (notes only, not highlights)
  const getPageAnnotations = useCallback((pageNum) => {
    return annotations.filter(a => a.page === pageNum);
  }, [annotations]);

  // Handle clicking on page annotation tab
  const handleAnnotationTabClick = (pageNum) => {
    setShowSidebar(true);
    // Find first annotation on this page
    const pageAnnotations = getPageAnnotations(pageNum);
    if (pageAnnotations.length > 0) {
      setFocusedAnnotationId(pageAnnotations[0].id);
      // Scroll to the annotation in sidebar after a short delay
      setTimeout(() => {
        const element = document.getElementById(`annotation-${pageAnnotations[0].id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add highlight animation
          element.classList.add('annotation-focused');
          setTimeout(() => {
            element.classList.remove('annotation-focused');
          }, 600);
        }
      }, 150);
    }
  };

  // Calculate visible pages based on view mode
  const getVisiblePages = useCallback(() => {
    if (viewMode === 'single') {
      return [currentPage];
    } else {
      // Double page view - show pairs
      const spreadIndex = Math.floor((currentPage - 1) / 2);
      const leftPage = spreadIndex * 2 + 1;
      const rightPage = leftPage + 1;
      const pages = [leftPage];
      if (rightPage <= numPages) {
        pages.push(rightPage);
      }
      return pages;
    }
  }, [currentPage, viewMode, numPages]);

  const visiblePages = getVisiblePages();

  // Keep the highlight popup fully visible (above header/footer, inside the viewport)
  useLayoutEffect(() => {
    if (!showHighlightPopup || !selectionPosition || !highlightPopupRef.current) { setPopupPos(null); return; }
    const { width: w, height: h } = highlightPopupRef.current.getBoundingClientRect();
    const margin = 8;
    const frame = pagesContainerRef.current?.getBoundingClientRect();
    const topLimit = (frame ? frame.top : 56) + margin;
    const bottomLimit = (frame ? frame.bottom : window.innerHeight - 56) - margin;
    // On touch screens the system selection toolbar sits above the text, so place ours below it
    const touch = isRecentTouch();
    let top = touch ? selectionPosition.vBottom + 28 : selectionPosition.vTop - h - 10;
    if (touch && top + h > bottomLimit) top = selectionPosition.vTop - h - 10;
    if (top < topLimit) top = selectionPosition.vBottom + 10;
    if (top + h > bottomLimit) top = Math.max(topLimit, bottomLimit - h);
    const left = Math.min(Math.max(margin, selectionPosition.vx - w / 2), window.innerWidth - w - margin);
    setPopupPos({ left, top });
  }, [showHighlightPopup, selectionPosition]);

  // Fit-to-frame: 100% zoom = pages fully fit the available area (no scrollbars)
  const [pageSize, setPageSize] = useState(null);
  const [frameSize, setFrameSize] = useState(null);
  useEffect(() => {
    const el = pagesContainerRef.current;
    if (!el) return;
    const measure = () => setFrameSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, pdfUrl]);

  // Touch: pinch drives the reader's own zoom (50%–250%); one finger pans the zoomed page inside the frame
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const pageScrollRef = useRef(null);
  useEffect(() => {
    const el = pagesContainerRef.current;
    if (!el) return;
    let pinch = null;
    let pan = null;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const hasSelection = () => (window.getSelection()?.toString() || '').length > 0;
    const resetPinch = (wrap) => {
      if (!pinch || !wrap) return;
      wrap.style.overflow = pinch.ov;
      if (wrap.firstElementChild) wrap.firstElementChild.style.transform = '';
      pinch = null;
    };
    const onStart = (e) => {
      const wrap = pageScrollRef.current;
      if (e.touches.length === 1) {
        resetPinch(wrap);
        const t = e.touches[0];
        pan = wrap ? { x: t.clientX, y: t.clientY, sl: wrap.scrollLeft, st: wrap.scrollTop, t0: Date.now(), active: false } : null;
        return;
      }
      if (e.touches.length !== 2 || !wrap) return;
      e.preventDefault();
      pan = null;
      resetPinch(wrap);
      window.getSelection()?.removeAllRanges();
      const r = wrap.getBoundingClientRect();
      const fx = (e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left;
      const fy = (e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top;
      pinch = { d0: dist(e.touches), s0: scaleRef.current, next: scaleRef.current, fx, fy, sl: wrap.scrollLeft, st: wrap.scrollTop, ov: wrap.style.overflow };
      wrap.style.overflow = 'visible';
      const doc = wrap.firstElementChild;
      if (doc) doc.style.transformOrigin = `${fx + wrap.scrollLeft}px ${fy + wrap.scrollTop}px`;
    };
    const onMove = (e) => {
      const wrap = pageScrollRef.current;
      if (pinch && e.touches.length === 2) {
        e.preventDefault();
        pinch.next = Math.min(2.5, Math.max(0.5, pinch.s0 * (dist(e.touches) / pinch.d0)));
        const doc = wrap?.firstElementChild;
        if (doc) doc.style.transform = `scale(${pinch.next / pinch.s0})`;
        return;
      }
      if (!pan || !wrap || e.touches.length !== 1 || scaleRef.current <= 1) return;
      const canPan = wrap.scrollWidth > wrap.clientWidth + 1 || wrap.scrollHeight > wrap.clientHeight + 1;
      if (!canPan) return;
      const t = e.touches[0];
      const dx = t.clientX - pan.x, dy = t.clientY - pan.y;
      if (!pan.active) {
        // A long press is a text selection, not a drag
        if (hasSelection() || Date.now() - pan.t0 > 400 || Math.hypot(dx, dy) < 8) return;
        pan.active = true;
      }
      e.preventDefault();
      wrap.scrollLeft = pan.sl - dx;
      wrap.scrollTop = pan.st - dy;
    };
    const onEnd = (e) => {
      if (e.touches.length === 0) pan = null;
      if (!pinch || e.touches.length >= 2) return;
      const { s0, fx, fy, sl, st, ov } = pinch;
      const next = Math.round(pinch.next * 20) / 20;
      pinch = null;
      const wrap = pageScrollRef.current;
      if (!wrap) return;
      // Restore React's inline value; '' would leave overflow 'visible' when scale's overflow mode didn't change
      wrap.style.overflow = ov;
      const doc = wrap.firstElementChild;
      if (doc) doc.style.transform = '';
      setShowHighlightPopup(false);
      setScale(next);
      const k = next / s0;
      setTimeout(() => {
        wrap.scrollLeft = (sl + fx) * k - fx;
        wrap.scrollTop = (st + fy) * k - fy;
      }, 120);
    };
    const blockGesture = (e) => e.preventDefault();
    el.addEventListener('touchstart', onStart, { passive: false });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    document.addEventListener('gesturestart', blockGesture);
    document.addEventListener('gesturechange', blockGesture);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      document.removeEventListener('gesturestart', blockGesture);
      document.removeEventListener('gesturechange', blockGesture);
    };
  }, [loading, pdfUrl]);

  const fitScale = useMemo(() => {
    if (!pageSize || !frameSize) return 1;
    const padY = isFullscreen ? 16 : 48;
    const padX = isCompact ? 24 : (isFullscreen ? 140 : 180);
    const cols = viewMode === 'double' ? 2 : 1;
    const byW = (frameSize.w - padX) / (pageSize.w * cols + 4 * (cols - 1));
    const byH = (frameSize.h - padY) / pageSize.h;
    return Math.max(0.1, Math.min(byW, byH) * 0.995);
  }, [pageSize, frameSize, isFullscreen, viewMode, isCompact]);

  // Extract all text from PDF when document loads
  const extractPdfText = useCallback(async (pdf) => {
    const textByPage = {};
    
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      try {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        textByPage[pageNum] = pageText;
      } catch (err) {
        console.warn(`Error extracting text from page ${pageNum}:`, err);
        textByPage[pageNum] = '';
      }
    }
    
    setPdfTextByPage(textByPage);
    return textByPage;
  }, []);

  // Highlight search matches in DOM - only the exact term, zoom-independent
  const highlightSearchInDOM = useCallback((query) => {
    document.querySelectorAll('.search-highlight').forEach(el => el.remove());
    
    if (!query) return;
    
    const queryLower = query.toLowerCase();
    const textLayers = document.querySelectorAll('.react-pdf__Page__textContent');
    
    textLayers.forEach((layer) => {
      const pageEl = layer.closest('.react-pdf__Page');
      if (!pageEl) return;
      
      const pageRect = pageEl.getBoundingClientRect();
      const pageWidth = pageRect.width;
      const pageHeight = pageRect.height;
      
      if (pageWidth === 0 || pageHeight === 0) return;
      
      const spans = layer.querySelectorAll('span');
      
      spans.forEach(span => {
        const text = span.textContent;
        const textLower = text.toLowerCase();
        
        let startIndex = 0;
        while (true) {
          const index = textLower.indexOf(queryLower, startIndex);
          if (index === -1) break;
          
          const textNode = span.firstChild;
          if (textNode && textNode.nodeType === Node.TEXT_NODE) {
            try {
              const range = document.createRange();
              range.setStart(textNode, index);
              range.setEnd(textNode, index + query.length);
              
              const rects = range.getClientRects();
              for (let i = 0; i < rects.length; i++) {
                const rect = rects[i];
                
                // Calculate position relative to current page dimensions
                const left = rect.left - pageRect.left;
                const top = rect.top - pageRect.top;
                
                const highlightEl = document.createElement('div');
                highlightEl.className = 'search-highlight';
                highlightEl.style.cssText = `
                  position: absolute;
                  left: ${left}px;
                  top: ${top}px;
                  width: ${rect.width}px;
                  height: ${rect.height}px;
                  background-color: rgba(255, 200, 0, 0.6);
                  pointer-events: none;
                  z-index: 15;
                  border-radius: 2px;
                  box-shadow: 0 0 3px rgba(255, 200, 0, 0.8);
                `;
                
                pageEl.style.position = 'relative';
                pageEl.appendChild(highlightEl);
              }
            } catch (e) {
              console.warn('Range API failed:', e);
            }
          }
          
          startIndex = index + 1;
        }
      });
    });
  }, []);
  const onTextLayerRendered = useCallback(() => {
    if (activeSearchTerm) highlightSearchInDOM(activeSearchTerm);
  }, [activeSearchTerm, highlightSearchInDOM]);
  useEffect(() => {
    if (pageScrollRef.current) { pageScrollRef.current.scrollLeft = 0; pageScrollRef.current.scrollTop = 0; }
  }, [currentPage]);


  // Search in all pages using extracted text
  const handleSearch = useCallback(async () => {
    if (!searchText.trim() || searchText.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    const results = [];
    const query = searchText.trim().replace(/\s+/g, ' ');
    const queryFold = foldText(query);
    
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const pageText = pdfTextByPage[pageNum] || '';
      if (!pageText) continue;
      const pageFold = foldText(pageText);
      let pos = pageFold.indexOf(queryFold);
      while (pos !== -1) {
        const textBefore = pageText.substring(0, pos);
        const textAfter = pageText.substring(pos + query.length);
        const wordsBefore = textBefore.split(/\s+/).filter(w => w.length > 0);
        const wordsAfter = textAfter.split(/\s+/).filter(w => w.length > 0);
        const isStartOfSentence = pos === 0 || /[.!?]\s*$/.test(textBefore.trim());
        results.push({
          id: `${pageNum}-${pos}`,
          page: pageNum,
          position: pos,
          context: {
            before: isStartOfSentence ? '' : wordsBefore.slice(-30).join(' '),
            match: pageText.substring(pos, pos + query.length),
            after: wordsAfter.slice(0, isStartOfSentence ? 50 : 30).join(' ')
          }
        });
        pos = pageFold.indexOf(queryFold, pos + Math.max(1, queryFold.length));
      }
    }
    
    setSearchResults(results);
    setActiveSearchTerm(searchText);
    setIsSearching(false);
  }, [searchText, pdfTextByPage, numPages]);

  // Re-run the active search as more pages get indexed
  useEffect(() => {
    if (activeSearchTerm && searchText === activeSearchTerm) handleSearch();
    // eslint-disable-next-line
  }, [pdfTextByPage]);

  // Navigate to a search result
  const goToSearchResult = useCallback((result) => {
    goToPage(result.page);
    setTimeout(() => highlightSearchInDOM(activeSearchTerm), 500);
  }, [activeSearchTerm, highlightSearchInDOM]);

  // Close search sidebar
  const closeSearch = useCallback(() => {
    setShowSearchSidebar(false);
    setSearchText('');
    setSearchResults([]);
    setActiveSearchTerm('');
    document.querySelectorAll('.search-highlight').forEach(el => el.remove());
  }, []);

  // Load book data
  useEffect(() => {
    let isMounted = true;
    
    const loadBook = async () => {
      try {
        if (!token) {
          navigate(`/${editorSlug}/login?redirect=/${editorSlug}/livro/${bookId}`);
          return;
        }

        const catalogResponse = await axios.get(`${API}/digital/${editorId}/catalog`);
        if (!isMounted) return;
        
        // Save editor name
        if (catalogResponse.data.editor?.nome) {
          setEditorName(catalogResponse.data.editor.nome);
        }
        
        const bookData = catalogResponse.data.books?.find(b => b.id === bookId);
        
        if (!bookData) {
          setError('Livro não encontrado');
          setLoading(false);
          return;
        }
        
        setBook(bookData);
        setPdfUrl(`${API}/digital/books/${bookId}/read?auth=${token}`);
        
        // Load annotations and highlights
        try {
          const annotationsRes = await axios.get(`${API}/digital/books/${bookId}/annotations?auth=${token}`);
          if (isMounted) {
            const allAnnotations = annotationsRes.data.annotations || [];
            setAnnotations(allAnnotations.filter(a => a.type === 'note'));
            setHighlights(allAnnotations.filter(a => a.type === 'highlight'));
          }
        } catch (e) {
          console.log('No annotations yet');
        }
        
        // Load reading progress
        try {
          const progressRes = await axios.get(`${API}/digital/books/${bookId}/progress?auth=${token}`);
          if (isMounted && progressRes.data.current_page > 0) {
            setCurrentPage(progressRes.data.current_page);
          }
        } catch (e) {
          console.log('No progress yet');
        }
        
        // Check if book is favorite
        try {
          const favoriteRes = await axios.get(`${API}/digital/${editorId}/subscriber/favorites/${bookId}/check?auth=${token}`);
          if (isMounted) {
            setIsFavorite(favoriteRes.data.is_favorite);
          }
        } catch (e) {
          console.log('Error checking favorite:', e);
        }
        
        if (isMounted) {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading book:', err);
        if (!isMounted) return;
        
        if (reloadIfSuspended(err)) return;
        if (err.response?.status === 401 || err.response?.status === 403) {
          navigate(`/${editorSlug}/login?redirect=/${editorSlug}/livro/${bookId}`);
        } else {
          const detail = err.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : (detail?.message ? `${detail.message} ${detail.email || ''}` : 'Erro ao carregar livro'));
        }
        setLoading(false);
      }
    };

    loadBook();
    
    return () => {
      isMounted = false;
    };
  }, [editorId, bookId, navigate, token]);

  // Save progress when page changes
  useEffect(() => {
    if (numPages > 0 && token) {
      axios.post(`${API}/digital/books/${bookId}/progress?auth=${token}&current_page=${currentPage}&total_pages=${numPages}&pages=${visiblePages.join(',')}`)
        .catch(e => console.log('Error saving progress:', e));
    }
  }, [currentPage, numPages, bookId, token, viewMode]); // eslint-disable-line

  // Toggle favorite
  const toggleFavorite = async () => {
    if (!token || loadingFavorite) return;
    
    setLoadingFavorite(true);
    try {
      if (isFavorite) {
        await axios.delete(`${API}/digital/${editorId}/subscriber/favorites/${bookId}?auth=${token}`);
        setIsFavorite(false);
      } else {
        await axios.post(`${API}/digital/${editorId}/subscriber/favorites/${bookId}?auth=${token}`);
        setIsFavorite(true);
      }
    } catch (e) {
      console.error('Error toggling favorite:', e);
    } finally {
      setLoadingFavorite(false);
    }
  };

  const onDocumentLoadSuccess = useCallback(async (pdf) => {
    setNumPages(pdf.numPages);
    setPdfDoc(pdf);
    try {
      const firstPage = await pdf.getPage(1);
      const vp = firstPage.getViewport({ scale: 1 });
      setPageSize({ w: vp.width, h: vp.height });
    } catch (err) {
      console.warn('Could not read page size', err);
    }
    
    // Prefer the server-side full-text index (more accurate); fall back to in-browser extraction
    setTextIndexProgress(0);
    try {
      const { data } = await axios.get(`${API}/digital/books/${bookId}/page-texts?auth=${token}`);
      if (Array.isArray(data.pages) && data.pages.length === pdf.numPages) {
        const serverText = {};
        data.pages.forEach((t, i) => { serverText[i + 1] = t; });
        setPdfTextByPage(serverText);
        setTextIndexProgress(pdf.numPages);
        return;
      }
    } catch (err) {
      console.warn('Server text index unavailable, extracting in browser', err);
    }
    const textByPage = {};
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      try {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        textByPage[pageNum] = buildPageText(textContent.items);
      } catch (err) {
        console.warn(`Error extracting text from page ${pageNum}:`, err);
        textByPage[pageNum] = '';
      }
      if (pageNum % 25 === 0 || pageNum === pdf.numPages) {
        setPdfTextByPage({ ...textByPage });
        setTextIndexProgress(pageNum);
      }
    }
  }, [bookId, token]);

  // Navigation
  const goToPrevious = () => {
    if (viewMode === 'single') {
      if (currentPage > 1) setCurrentPage(currentPage - 1);
    } else {
      if (currentPage > 2) setCurrentPage(currentPage - 2);
      else setCurrentPage(1);
    }
  };

  const goToNext = () => {
    if (viewMode === 'single') {
      if (currentPage < numPages) setCurrentPage(currentPage + 1);
    } else {
      if (currentPage + 2 <= numPages) setCurrentPage(currentPage + 2);
      else if (currentPage < numPages) setCurrentPage(numPages);
    }
  };

  const goToPage = (pageNum) => {
    const page = Math.max(1, Math.min(pageNum, numPages));
    setCurrentPage(page);
  };

  // Zoom controls
  const zoomIn = () => setScale(Math.min(scale + 0.25, 2.5));
  const zoomOut = () => setScale(Math.max(scale - 0.25, 0.5));

  // Fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      else if (e.key === 'ArrowRight') goToNext();
      else if (e.key === 'Escape') {
        setIsFullscreen(false);
        setShowHighlightPopup(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, numPages, viewMode]);

  // Handle text selection for highlighting
  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    // Get the EXACT text selected by user - do NOT modify or expand
    const text = selection.toString();
    
    // Accept any selection, even a single character (do not trim)
    if (text.length === 0) return;
    
    // Only text inside the PDF pages can be highlighted/cited (not toolbar, footer or panels)
    const insidePdfText = (node) => {
      const el = node && (node.nodeType === 1 ? node : node.parentElement);
      return Boolean(el && el.closest('.react-pdf__Page__textContent'));
    };
    if (!insidePdfText(selection.anchorNode) || !insidePdfText(selection.focusNode)) {
      setShowHighlightPopup(false);
      return;
    }
    
    // Store the exact selected text without modifications
    setSelectedText(text);
    
    // Get selection range and capture exact positions for precise highlighting
    const range = selection.getRangeAt(0);
    const rects = range.getClientRects(); // Get all rectangles of selection
    const containerRect = pagesContainerRef.current?.getBoundingClientRect();
    
    // Find which page the selection is on
    let selectedPageNum = visiblePages[0];
    const pageElements = pagesContainerRef.current?.querySelectorAll('.react-pdf__Page');
    let selectedPageEl = null;
    
    if (pageElements) {
      pageElements.forEach((pageEl, index) => {
        const pageRect = pageEl.getBoundingClientRect();
        // Check if first rect of selection is within this page
        if (rects.length > 0) {
          const firstRect = rects[0];
          if (firstRect.left >= pageRect.left && firstRect.right <= pageRect.right + 50) {
            selectedPageNum = visiblePages[index] || visiblePages[0];
            selectedPageEl = pageEl;
          }
        }
      });
    }
    
    // If no page found, use first visible
    if (!selectedPageEl) {
      selectedPageEl = pageElements?.[0];
    }
    
    const pageRect = selectedPageEl?.getBoundingClientRect();
    
    // Store selection rectangles as PERCENTAGES relative to page dimensions
    // This makes highlights zoom-independent
    const selectionRects = [];
    if (pageRect && pageRect.width > 0 && pageRect.height > 0) {
      for (let i = 0; i < rects.length; i++) {
        const rect = rects[i];
        // Convert to percentages for zoom-independence
        selectionRects.push({
          leftPercent: ((rect.left - pageRect.left) / pageRect.width) * 100,
          topPercent: ((rect.top - pageRect.top) / pageRect.height) * 100,
          widthPercent: (rect.width / pageRect.width) * 100,
          heightPercent: (rect.height / pageRect.height) * 100
        });
      }
    }
    
    if (containerRect && selectionRects.length > 0) {
      const boundingRect = range.getBoundingClientRect();
      setSelectionPosition({
        x: boundingRect.left - containerRect.left + boundingRect.width / 2,
        y: boundingRect.top - containerRect.top - 10,
        vx: boundingRect.left + boundingRect.width / 2,
        vTop: boundingRect.top,
        vBottom: boundingRect.bottom,
        pageNum: selectedPageNum,
        rects: selectionRects // Store as percentages for zoom-independence
      });
      setShowHighlightPopup(true);
    }
  }, [visiblePages]);

  useEffect(() => {
    let timer;
    // Phones/tablets select with long-press + handles and never fire mouseup
    const onSelectionChange = () => {
      if (!isRecentTouch()) return;
      clearTimeout(timer);
      timer = setTimeout(handleTextSelection, 450);
    };
    document.addEventListener('mouseup', handleTextSelection);
    document.addEventListener('selectionchange', onSelectionChange);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseup', handleTextSelection);
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, [handleTextSelection]);

  // Save highlight immediately (no comment option)
  const handleSaveHighlight = async () => {
    if (!selectedText) return;
    
    // Use the page number detected during selection
    const pageNum = selectionPosition?.pageNum || visiblePages[0];
    
    try {
      const response = await axios.put(
        `${API}/digital/books/${bookId}/annotations?auth=${token}`,
        {
          page: pageNum,
          type: 'highlight',
          text: selectedText,
          color: selectedColor,
          position: selectionPosition // Contains exact rects for precise highlighting
        }
      );
      
      setHighlights([...highlights, response.data.annotation]);
      setSelectedText('');
      setShowHighlightPopup(false);
      window.getSelection()?.removeAllRanges();
      
      // Highlights will be applied automatically via useEffect
    } catch (err) {
      console.error('Error saving highlight:', err);
    }
  };

  // Apply highlights visually to the text layer - called after PDF renders
  const applyHighlightsToPage = useCallback(() => {
    const pageElements = pagesContainerRef.current?.querySelectorAll('.react-pdf__Page');
    if (!pageElements || pageElements.length === 0) return;
    
    pageElements.forEach((pageEl, idx) => {
      const currentPageNum = visiblePages[idx];
      if (!currentPageNum) return;
      
      // Get highlights for this page
      const pageHighlights = highlights.filter(h => h.page === currentPageNum);
      
      // Clear previous custom highlights on this page
      pageEl.querySelectorAll('.custom-highlight').forEach(el => el.remove());
      
      // Get current page dimensions for percentage-to-pixel conversion
      const pageRect = pageEl.getBoundingClientRect();
      const pageWidth = pageRect.width;
      const pageHeight = pageRect.height;
      
      const textLayer = pageEl.querySelector('.react-pdf__Page__textContent');
      
      // Apply each highlight
      pageHighlights.forEach(highlight => {
        // Check if rects are stored as percentages (new format)
        const hasPercentageRects = highlight.position?.rects?.length > 0 && 
                                    highlight.position.rects[0].leftPercent !== undefined;
        
        if (hasPercentageRects) {
          // New format: use stored percentages for zoom-independent highlighting
          highlight.position.rects.forEach((rect, rectIdx) => {
            const highlightEl = document.createElement('div');
            highlightEl.className = 'custom-highlight';
            highlightEl.dataset.highlightId = highlight.id;
            highlightEl.dataset.rectIndex = rectIdx;
            
            // Convert percentages to current pixel values
            const left = (rect.leftPercent / 100) * pageWidth;
            const top = (rect.topPercent / 100) * pageHeight;
            const width = (rect.widthPercent / 100) * pageWidth;
            const height = (rect.heightPercent / 100) * pageHeight;
            
            highlightEl.style.cssText = `
              position: absolute;
              left: ${left}px;
              top: ${top}px;
              width: ${width}px;
              height: ${height}px;
              background-color: ${highlight.color};
              opacity: 0.2;
              pointer-events: none;
              z-index: 1;
              border-radius: 2px;
            `;
            
            pageEl.style.position = 'relative';
            pageEl.appendChild(highlightEl);
          });
        } else if (textLayer) {
          // Fallback for legacy highlights: find text in current text layer
          // This method is zoom-independent because it uses current DOM positions
          const searchText = highlight.text.toLowerCase();
          const textSpans = textLayer.querySelectorAll('span');
          let found = false;
          
          // First try exact match
          textSpans.forEach(span => {
            if (found) return;
            const spanText = span.textContent.toLowerCase();
            if (spanText.includes(searchText)) {
              const rect = span.getBoundingClientRect();
              
              const highlightEl = document.createElement('div');
              highlightEl.className = 'custom-highlight';
              highlightEl.dataset.highlightId = highlight.id;
              highlightEl.style.cssText = `
                position: absolute;
                left: ${rect.left - pageRect.left}px;
                top: ${rect.top - pageRect.top}px;
                width: ${rect.width}px;
                height: ${rect.height}px;
                background-color: ${highlight.color};
                opacity: 0.2;
                pointer-events: none;
                z-index: 1;
                border-radius: 2px;
              `;
              
              pageEl.style.position = 'relative';
              pageEl.appendChild(highlightEl);
              found = true;
            }
          });
          
          // If not found by substring, try partial match (for multi-word highlights)
          if (!found) {
            const searchWords = searchText.split(/\s+/).filter(w => w.length > 2);
            if (searchWords.length > 0) {
              textSpans.forEach(span => {
                const spanText = span.textContent.toLowerCase();
                // Match if span contains any significant word from the highlight
                if (searchWords.some(word => spanText.includes(word))) {
                  const rect = span.getBoundingClientRect();
                  
                  const highlightEl = document.createElement('div');
                  highlightEl.className = 'custom-highlight';
                  highlightEl.dataset.highlightId = highlight.id;
                  highlightEl.style.cssText = `
                    position: absolute;
                    left: ${rect.left - pageRect.left}px;
                    top: ${rect.top - pageRect.top}px;
                    width: ${rect.width}px;
                    height: ${rect.height}px;
                    background-color: ${highlight.color};
                    opacity: 0.2;
                    pointer-events: none;
                    z-index: 1;
                    border-radius: 2px;
                  `;
                  
                  pageEl.style.position = 'relative';
                  pageEl.appendChild(highlightEl);
                }
              });
            }
          }
        }
      });
    });
  }, [highlights, visiblePages]);

  // MutationObserver to apply highlights when PDF text layer is ready
  useEffect(() => {
    if (highlights.length === 0) return;
    
    const container = pagesContainerRef.current;
    if (!container) return;
    
    // Apply highlights immediately if text layer exists
    const attemptApply = () => {
      const textLayers = container.querySelectorAll('.react-pdf__Page__textContent');
      if (textLayers.length > 0) {
        applyHighlightsToPage();
        return true;
      }
      return false;
    };
    
    // Try immediately
    if (attemptApply()) return;
    
    // Use MutationObserver to detect when text layer is rendered
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const hasTextLayer = container.querySelector('.react-pdf__Page__textContent');
          if (hasTextLayer) {
            // Small delay to ensure text content is fully rendered
            setTimeout(() => {
              applyHighlightsToPage();
            }, 150);
            break;
          }
        }
      }
    });
    
    observer.observe(container, { 
      childList: true, 
      subtree: true 
    });
    
    return () => observer.disconnect();
  }, [visiblePages, highlights, applyHighlightsToPage]);
  
  // Reapply highlights when scale (zoom) changes - wait for PDF to re-render
  useEffect(() => {
    if (highlights.length > 0) {
      // Need longer delay for PDF.js to fully re-render at new scale
      const timer = setTimeout(() => {
        applyHighlightsToPage();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [scale, fitScale, applyHighlightsToPage, highlights.length]);

  // Reapply search highlights when scale (zoom) changes
  useEffect(() => {
    if (activeSearchTerm) {
      const timer = setTimeout(() => {
        highlightSearchInDOM(activeSearchTerm);
      }, 350); // Slightly longer than marker highlights to ensure text layer is ready
      return () => clearTimeout(timer);
    }
  }, [scale, fitScale, activeSearchTerm, highlightSearchInDOM]);

  // Add note
  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    
    try {
      const response = await axios.put(
        `${API}/digital/books/${bookId}/annotations?auth=${token}`,
        {
          page: notePage,
          type: 'note',
          note: noteText,
          color: selectedColor
        }
      );
      
      setAnnotations([...annotations, response.data.annotation]);
      setNoteText('');
      setShowNoteModal(false);
    } catch (err) {
      console.error('Error saving note:', err);
    }
  };

  // Delete annotation/highlight
  const handleDeleteAnnotation = async (annotationId, isHighlight = false) => {
    try {
      await axios.delete(`${API}/digital/books/${bookId}/annotations/${annotationId}?auth=${token}`);
      if (isHighlight) {
        setHighlights(highlights.filter(h => h.id !== annotationId));
      } else {
        setAnnotations(annotations.filter(a => a.id !== annotationId));
      }
    } catch (err) {
      console.error('Error deleting annotation:', err);
    }
  };

  // Get page info string
  const getPageInfo = () => {
    if (viewMode === 'single') {
      return `Página ${currentPage} de ${numPages}`;
    } else {
      const pages = visiblePages;
      if (pages.length === 2) {
        return `Páginas ${pages[0]}-${pages[1]} de ${numPages}`;
      }
      return `Página ${pages[0]} de ${numPages}`;
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        background: '#1a1f2e',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: '#68AAB0' }} />
        <p style={{ color: 'white' }}>Carregando livro...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        background: '#1a1f2e',
        color: 'white',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <AlertCircle className="w-16 h-16" style={{ color: '#ef4444' }} />
        <h2 style={{ fontSize: '1.25rem' }}>{error}</h2>
        <Button 
          onClick={() => navigate(`/${editorSlug}`)} 
          style={{ background: '#68AAB0' }}
        >
          Voltar ao Catálogo
        </Button>
      </div>
    );
  }

  // NOTE: no `bottom` here — App.css/index.css hide any div[style*="position: fixed"][style*="bottom"] (Emergent badge rule)
  const sideOverlay = isCompact ? { position: 'fixed', top: compactHeaderH, height: `calc(100% - ${compactHeaderH}px)`, left: 0, zIndex: 5500, boxShadow: '8px 0 30px rgba(0,0,0,0.35)' } : {};
  const sideWidth = (w) => (isCompact ? 'min(360px, 100vw)' : w);
  const openPanel = (setter, current) => {
    if (isCompact && !current) { setShowSearchSidebar(false); setShowSidebar(false); setShowTTSControls(false); setShowAIChat(false); }
    setter(!current);
  };
  const menuGroups = [
    [
      { id: 'search', label: 'Buscar no livro', icon: Search, active: showSearchSidebar, onClick: () => openPanel(setShowSearchSidebar, showSearchSidebar) },
      ...(aiEnabled ? [{ id: 'ai-chat', label: 'Assistente do livro (ChatGPT)', icon: Bot, iconColor: AI_GOLD, active: showAIChat, onClick: () => openPanel(setShowAIChat, showAIChat) }] : []),
      { id: 'tts', label: 'Leitura em voz alta', icon: Volume2, active: showTTSControls, onClick: () => openPanel(setShowTTSControls, showTTSControls) }
    ],
    [
      { id: 'view-mode', label: viewMode === 'single' ? 'Ver 2 páginas' : 'Ver 1 página', icon: viewMode === 'single' ? BookOpenCheck : File, onClick: () => setViewMode(viewMode === 'single' ? 'double' : 'single') },
      { id: 'zoom' }
    ],
    [
      { id: 'add-note', label: 'Adicionar nota', icon: StickyNote, onClick: () => { setNotePage(visiblePages[0] || 1); setShowNoteModal(true); } },
      { id: 'annotations', label: 'Minhas anotações e grifos', icon: Bookmark, active: showSidebar, onClick: () => openPanel(setShowSidebar, showSidebar) },
      { id: 'favorite', label: isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos', icon: Heart, iconColor: isFavorite ? '#e53e3e' : undefined, fill: isFavorite ? '#e53e3e' : undefined, disabled: loadingFavorite, keepOpen: true, onClick: toggleFavorite },
      { id: 'copy-link', label: linkCopied ? 'Link copiado!' : 'Copiar link do livro', icon: linkCopied ? Check : Link2, keepOpen: true, onClick: copyBookLink }
    ],
    [
      { id: 'dark-mode', label: isDarkMode ? 'Modo claro' : 'Modo noturno', icon: isDarkMode ? Sun : Moon, keepOpen: true, onClick: () => setIsDarkMode(!isDarkMode) },
      { id: 'fullscreen', label: 'Tela cheia', icon: Maximize, onClick: toggleFullscreen },
      { id: 'report', label: 'Denunciar conteúdo', icon: Flag, iconColor: '#f56565', onClick: () => setShowReport(true) }
    ]
  ];

  return (
    <div 
      ref={containerRef}
      style={{ 
        height: '100vh', 
        display: 'flex', 
        flexDirection: 'column', 
        background: isDarkMode ? '#1a1a2e' : '#e8e8e8',
        overflow: 'hidden'
      }}
    >
      {/* CSS Animation for annotation highlight and search */}
      <style>
        {`
          @keyframes pulse-highlight {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.02); }
          }
          .annotation-focused {
            animation: pulse-highlight 0.3s ease-in-out 2;
          }
          @keyframes searchPulse {
            0% { opacity: 0.3; transform: scale(0.98); }
            50% { opacity: 0.7; transform: scale(1.02); }
            100% { opacity: 0.5; transform: scale(1); }
          }
        `}
      </style>
      
      {/* Header */}
      {isCompact ? (
        <header ref={compactHeaderRef} data-testid="reader-compact-header" style={{
          display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.35rem 0.5rem',
          paddingTop: 'max(0.35rem, env(safe-area-inset-top))',
          background: isDarkMode ? '#0f0f1a' : '#1a1f2e', color: 'white', borderBottom: '1px solid #2d3748', zIndex: 100, flexShrink: 0
        }}>
          <button onClick={() => setShowControlsMenu(true)} data-testid="reader-menu-btn" title="Controles" style={compactIconBtn}>
            <Menu className="w-5 h-5" />
          </button>
          <button onClick={() => navigate(`/${editorSlug}`)} data-testid="back-btn" title="Voltar" style={compactIconBtn}>
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div style={{ flex: 1, minWidth: 0, padding: '0 0.25rem' }}>
            <h1 title={book?.titulo} style={{ fontSize: '0.9rem', fontWeight: 600, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book?.titulo}</h1>
            <p data-testid="reader-compact-page-info" style={{ fontSize: '0.7rem', color: '#a0aec0', margin: 0, whiteSpace: 'nowrap' }}>{getPageInfo()}</p>
          </div>
          {aiEnabled && (
            <button onClick={() => openPanel(setShowAIChat, showAIChat)} data-testid="ai-chat-btn" title="Assistente do livro (ChatGPT · OpenAI GPT-5.4)"
              style={{ ...compactIconBtn, color: AI_GOLD, background: showAIChat ? 'rgba(212,160,23,0.2)' : 'transparent' }}>
              <Bot className="w-5 h-5" />
            </button>
          )}
        </header>
      ) : (
      <header style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        padding: '0.5rem 1rem',
        background: isDarkMode ? '#0f0f1a' : '#1a1f2e',
        color: 'white',
        borderBottom: '1px solid #2d3748',
        zIndex: 100,
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate(`/${editorSlug}`)}
            style={{ color: '#a0aec0' }}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div style={{ borderLeft: '1px solid #4a5568', paddingLeft: '1rem', width: 'clamp(160px, 24vw, 340px)', minWidth: 0 }} data-testid="reader-title-box">
            <h1 title={book?.titulo} data-testid="reader-book-title" style={{ fontSize: '1rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book?.titulo}</h1>
            <p title={book?.autor} data-testid="reader-book-author" style={{ fontSize: '0.75rem', color: '#a0aec0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{book?.autor}</p>
          </div>
        </div>

        {/* Center - Page Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.85rem', color: '#a0aec0' }}>
            {getPageInfo()}
          </span>
        </div>

        {/* Right - Tools */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          {/* Search Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowSearchSidebar(!showSearchSidebar)} 
            style={{ color: showSearchSidebar ? '#68AAB0' : '#a0aec0' }}
            title="Buscar no livro"
          >
            <Search className="w-4 h-4" />
          </Button>
          
          <div style={{ borderLeft: '1px solid #4a5568', height: '20px', margin: '0 0.25rem' }} />
          
          {/* View Mode Toggle */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setViewMode(viewMode === 'single' ? 'double' : 'single')}
            style={{ color: '#a0aec0' }}
            title={viewMode === 'single' ? 'Visualizar 2 páginas' : 'Visualizar 1 página'}
          >
            {viewMode === 'single' ? <BookOpenCheck className="w-4 h-4" /> : <File className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={zoomOut} style={{ color: '#a0aec0' }} data-testid="zoom-out-btn" title="Diminuir zoom">
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span style={{ fontSize: '0.85rem', color: '#a0aec0', minWidth: '45px', textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </span>
          <Button variant="ghost" size="sm" onClick={zoomIn} style={{ color: '#a0aec0' }} data-testid="zoom-in-btn" title="Aumentar zoom">
            <ZoomIn className="w-4 h-4" />
          </Button>
          
          <div style={{ borderLeft: '1px solid #4a5568', height: '20px', margin: '0 0.25rem' }} />
          
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setNotePage(visiblePages[0] || 1);
              setShowNoteModal(true);
            }}
            style={{ color: '#a0aec0' }}
            title="Adicionar nota"
          >
            <StickyNote className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowSidebar(!showSidebar)}
            style={{ color: showSidebar ? '#68AAB0' : '#a0aec0' }}
            title="Minhas anotações e grifos"
          >
            <Bookmark className="w-4 h-4" />
          </Button>
          
          {/* Copy link Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={copyBookLink}
            style={{ color: linkCopied ? '#68AAB0' : '#a0aec0', gap: '0.35rem', background: 'transparent' }}
            title="Copiar link de acesso a este livro"
            data-testid="copy-book-link-btn"
          >
            {linkCopied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
            <span style={{ fontSize: '0.8rem' }} data-testid="copy-book-link-label">{linkCopied ? 'Copiado!' : 'Link'}</span>
          </Button>
          
          {/* Favorite Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={toggleFavorite}
            disabled={loadingFavorite}
            style={{ 
              color: isFavorite ? '#e53e3e' : '#a0aec0',
              position: 'relative'
            }}
            title={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
            data-testid="favorite-btn"
          >
            <Heart 
              className="w-4 h-4" 
              style={{ 
                fill: isFavorite ? '#e53e3e' : 'none',
                transition: 'all 0.2s ease'
              }} 
            />
          </Button>
          
          <div style={{ borderLeft: '1px solid #4a5568', height: '20px', margin: '0 0.25rem' }} />
          
          {/* Text-to-Speech Button */}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => setShowTTSControls(!showTTSControls)}
            style={{ 
              color: isTTSActive ? '#68AAB0' : '#a0aec0',
              ...(isTTSActive ? { background: 'rgba(104,170,176,0.2)' } : {})
            }}
            title="Leitura em voz alta"
            data-testid="tts-btn"
          >
            <Volume2 className="w-4 h-4" />
          </Button>
          
          {aiEnabled && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAIChat(!showAIChat)}
              style={{ color: AI_GOLD, ...(showAIChat ? { background: 'rgba(212,160,23,0.2)' } : {}) }}
              title="Assistente do livro (ChatGPT · OpenAI GPT-5.4)"
              data-testid="ai-chat-btn"
            >
              <Bot className="w-5 h-5" />
            </Button>
          )}
          
          <div style={{ borderLeft: '1px solid #4a5568', height: '20px', margin: '0 0.25rem' }} />
          
          <Button variant="ghost" size="sm" onClick={() => setIsDarkMode(!isDarkMode)} style={{ color: '#a0aec0' }} data-testid="dark-mode-btn" title="Modo noturno">
            {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={toggleFullscreen} style={{ color: '#a0aec0' }} data-testid="fullscreen-btn" title="Tela cheia">
            <Maximize className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowReport(true)} style={{ color: '#f56565' }} data-testid="report-content-btn" title="Denunciar conteúdo">
            <Flag className="w-4 h-4" />
          </Button>
        </div>
      </header>
      )}
      {showReport && (
        <ReportContentModal bookId={bookId} bookTitle={book?.titulo} token={token} isDarkMode={isDarkMode} onClose={() => setShowReport(false)} />
      )}
      <ReaderControlsMenu
        open={isCompact && showControlsMenu}
        onClose={() => setShowControlsMenu(false)}
        groups={menuGroups}
        isDarkMode={isDarkMode}
        scale={scale}
        zoomIn={zoomIn}
        zoomOut={zoomOut}
        title={book?.titulo}
      />

      {/* Main Content Area */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        
        {/* TTS Sidebar - Left */}
        {showTTSControls && (
          <div style={{ 
            width: sideWidth('280px'), ...sideOverlay,
            background: isDarkMode ? '#0f0f1a' : 'white',
            borderRight: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0
          }}
          data-testid="tts-controls-panel"
          >
            {/* TTS Header */}
            <div style={{ 
              padding: '1rem', 
              borderBottom: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`,
              background: isDarkMode ? '#1a1a2e' : '#f7fafc'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Volume2 className="w-4 h-4" style={{ color: '#68AAB0' }} />
                  <span style={{ color: isDarkMode ? '#e2e8f0' : '#2d3748', fontSize: '0.9rem', fontWeight: 600 }}>
                    Leitura em Voz Alta
                  </span>
                </div>
                <button 
                  onClick={() => {
                    setShowTTSControls(false);
                    if (!isTTSActive) stopTTS();
                  }}
                  data-testid="tts-close-btn"
                  style={{ background: 'none', border: 'none', color: '#718096', cursor: 'pointer' }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* TTS Controls */}
            <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Play/Pause Button */}
              <Button 
                onClick={toggleTTSPlayPause}
                style={{ 
                  background: isTTSPlaying 
                    ? 'linear-gradient(135deg, #68AAB0 0%, #5a9a9f 100%)' 
                    : 'transparent',
                  color: isTTSPlaying ? 'white' : '#68AAB0',
                  border: '2px solid #68AAB0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  padding: '0.75rem'
                }}
                data-testid="tts-play-pause"
              >
                {isTTSPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                {isTTSPlaying ? 'Pausar' : (isTTSActive ? 'Continuar' : 'Iniciar Leitura')}
              </Button>
              <Button
                onClick={resetTTS}
                disabled={!isTTSActive}
                variant="outline"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%', padding: '0.75rem', marginTop: '-0.5rem',
                  background: 'transparent', color: isDarkMode ? '#fc8181' : '#e53e3e', border: `2px solid ${isDarkMode ? '#fc8181' : '#e53e3e'}` }}
                title="Parar e voltar ao início da página"
                data-testid="tts-stop-btn"
              >
                <Square className="w-4 h-4" style={{ fill: 'currentColor' }} />
                Parar
              </Button>

              {/* Speed Control */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Gauge className="w-4 h-4" style={{ color: isDarkMode ? '#a0aec0' : '#64748b' }} />
                  <span style={{ color: isDarkMode ? '#a0aec0' : '#64748b', fontSize: '0.85rem' }}>Velocidade</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.3rem' }}>
                  {[1, 1.25, 1.5, 2, 3, 4].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => changeTTSRate(rate)}
                      style={{
                        minHeight: '40px',
                        padding: '0.5rem 0.2rem',
                        borderRadius: '6px',
                        whiteSpace: 'nowrap',
                        border: ttsRate === rate ? '2px solid #68AAB0' : `1px solid ${isDarkMode ? '#4a5568' : '#cbd5e1'}`,
                        background: ttsRate === rate ? 'rgba(104,170,176,0.2)' : 'transparent',
                        color: ttsRate === rate ? '#68AAB0' : (isDarkMode ? '#a0aec0' : '#64748b'),
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: ttsRate === rate ? 600 : 400
                      }}
                      data-testid={`tts-speed-${rate}x`}
                    >
                      {String(rate).replace('.', ',')}x
                    </button>
                  ))}
                </div>
              </div>

              {/* Line Navigation */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <span style={{ color: isDarkMode ? '#a0aec0' : '#64748b', fontSize: '0.85rem' }}>Navegação</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <button
                    onClick={previousLine}
                    disabled={currentLineIndex <= 0 || ttsLines.length === 0}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: `1px solid ${isDarkMode ? '#4a5568' : '#cbd5e1'}`,
                      background: currentLineIndex > 0 && ttsLines.length > 0 ? 'rgba(104,170,176,0.1)' : 'transparent',
                      color: currentLineIndex > 0 && ttsLines.length > 0 ? '#68AAB0' : (isDarkMode ? '#4a5568' : '#a0aec0'),
                      cursor: currentLineIndex > 0 && ttsLines.length > 0 ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.25rem'
                    }}
                    title="Linha anterior"
                    data-testid="tts-prev-line"
                  >
                    <SkipBack className="w-4 h-4" />
                  </button>
                  
                  <span style={{ 
                    minWidth: '60px', 
                    textAlign: 'center',
                    color: isDarkMode ? '#e2e8f0' : '#2d3748',
                    fontSize: '0.9rem',
                    fontWeight: 500
                  }}>
                    {ttsLines.length > 0 ? `${currentLineIndex + 1}/${ttsLines.length}` : '-/-'}
                  </span>
                  
                  <button
                    onClick={nextLine}
                    disabled={currentLineIndex >= ttsLines.length - 1 || ttsLines.length === 0}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      borderRadius: '6px',
                      border: `1px solid ${isDarkMode ? '#4a5568' : '#cbd5e1'}`,
                      background: currentLineIndex < ttsLines.length - 1 && ttsLines.length > 0 ? 'rgba(104,170,176,0.1)' : 'transparent',
                      color: currentLineIndex < ttsLines.length - 1 && ttsLines.length > 0 ? '#68AAB0' : (isDarkMode ? '#4a5568' : '#a0aec0'),
                      cursor: currentLineIndex < ttsLines.length - 1 && ttsLines.length > 0 ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.25rem'
                    }}
                    title="Próxima linha"
                    data-testid="tts-next-line"
                  >
                    <SkipForward className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* TTS Word Highlight Display - Takes all remaining space */}
            <div 
              style={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                background: isDarkMode ? '#1a1a2e' : '#f8fafc',
                borderTop: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`
              }}
              data-testid="tts-text-display"
            >
              <div style={{ 
                padding: '0.75rem 1rem',
                borderBottom: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`,
                fontSize: '0.8rem',
                color: isDarkMode ? '#a0aec0' : '#64748b',
                fontWeight: 500
              }}>
                Texto sendo lido:
              </div>
              <div style={{ 
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                padding: '1rem'
              }}>
                {isTTSActive && currentLineWords.length > 0 ? (
                  <p style={{ 
                    fontSize: '1rem', 
                    lineHeight: '1.8',
                    color: isDarkMode ? '#e2e8f0' : '#2d3748',
                    margin: 0,
                    textAlign: 'justify',
                    wordBreak: 'break-word',
                    hyphens: 'auto'
                  }}>
                    {currentLineWords.map((word, index) => (
                      <span 
                        key={index}
                        style={{
                          padding: '2px 4px',
                          marginRight: '3px',
                          borderRadius: '4px',
                          backgroundColor: index === currentWordIndex 
                            ? '#68AAB0'
                            : 'transparent',
                          color: index === currentWordIndex ? 'white' : 'inherit',
                          transition: 'all 0.15s ease',
                          display: 'inline'
                        }}
                      >
                        {word}
                      </span>
                    )).reduce((acc, el, i) => (i === 0 ? [el] : [...acc, ' ', el]), [])}
                  </p>
                ) : (
                  <p style={{ 
                    color: '#a0aec0',
                    fontSize: '0.9rem',
                    textAlign: 'center',
                    margin: 0
                  }}>
                    Clique em "Iniciar Leitura" para começar
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Search Sidebar - Left */}
        {showSearchSidebar && (
          <div style={{ 
            width: sideWidth('350px'), ...sideOverlay,
            background: isDarkMode ? '#0f0f1a' : 'white',
            borderRight: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0
          }}>
            {/* Search Header */}
            <div style={{ 
              padding: '1rem', 
              borderBottom: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`,
              background: isDarkMode ? '#1a1a2e' : '#f7fafc'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ fontWeight: 600, color: isDarkMode ? 'white' : '#2d3748', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Search className="w-4 h-4" /> Buscar no Livro
                </h3>
                <button 
                  onClick={closeSearch}
                  data-testid="search-close-btn"
                  style={{ background: 'none', border: 'none', color: isDarkMode ? '#cbd5e0' : '#718096', cursor: 'pointer' }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Search Input */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Input
                  placeholder="Digite o termo..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                    if (e.key === 'Escape') closeSearch();
                  }}
                  style={{ flex: 1, ...(isDarkMode ? { color: '#ffffff', caretColor: '#ffffff', borderColor: '#4a5568', background: '#0f0f1a' } : {}) }}
                  className={isDarkMode ? 'placeholder:text-slate-400' : undefined}
                  autoFocus
                  data-testid="search-input"
                />
                <Button 
                  onClick={handleSearch}
                  disabled={isSearching || searchText.length < 2}
                  style={{ background: '#68AAB0', color: 'white' }}
                  data-testid="search-button"
                >
                  {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Results count */}
              {numPages > 0 && textIndexProgress < numPages && (
                <div data-testid="search-index-progress" style={{ fontSize: '0.75rem', color: '#b7791f', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Indexando o livro: {textIndexProgress} de {numPages} páginas
                </div>
              )}
              {searchResults.length > 0 && (
                <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#68AAB0', fontWeight: 500 }}>
                  {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''} encontrado{searchResults.length !== 1 ? 's' : ''}
                </p>
              )}
              {activeSearchTerm && searchResults.length === 0 && !isSearching && (
                <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#ef4444' }}>
                  Nenhum resultado para "{activeSearchTerm}"
                </p>
              )}
            </div>
            
            {/* Search Results List */}
            <div style={{ flex: 1, overflow: 'auto', padding: '0.5rem' }}>
              {searchResults.map((result, index) => (
                <div
                  key={result.id}
                  onClick={() => { goToSearchResult(result); if (isCompact) setShowSearchSidebar(false); }}
                  style={{
                    padding: '0.75rem',
                    marginBottom: '0.5rem',
                    borderRadius: '8px',
                    background: isDarkMode ? '#1a1a2e' : '#f7fafc',
                    cursor: 'pointer',
                    border: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isDarkMode ? '#2d3748' : '#edf2f7';
                    e.currentTarget.style.borderColor = '#68AAB0';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isDarkMode ? '#1a1a2e' : '#f7fafc';
                    e.currentTarget.style.borderColor = isDarkMode ? '#2d3748' : '#e2e8f0';
                  }}
                  data-testid={`search-result-${index}`}
                >
                  {/* Page number */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    marginBottom: '0.5rem'
                  }}>
                    <span style={{ 
                      background: '#68AAB0', 
                      color: 'white', 
                      padding: '0.15rem 0.5rem', 
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}>
                      Pág. {result.page}
                    </span>
                  </div>
                  
                  {/* Context with highlighted term */}
                  <p style={{ 
                    fontSize: '0.85rem', 
                    color: isDarkMode ? '#e2e8f0' : '#4a5568',
                    lineHeight: '1.5',
                    margin: 0,
                    wordBreak: 'break-word'
                  }}>
                    {result.context.before && (
                      <span style={{ color: isDarkMode ? '#a0aec0' : '#718096' }}>
                        ...{result.context.before}{' '}
                      </span>
                    )}
                    <mark style={{ 
                      background: 'rgba(255, 200, 0, 0.4)', 
                      padding: '0.1rem 0.2rem',
                      borderRadius: '2px',
                      fontWeight: 600,
                      color: isDarkMode ? '#ffd700' : '#b7791f'
                    }}>
                      {result.context.match}
                    </mark>
                    {result.context.after && (
                      <span style={{ color: isDarkMode ? '#a0aec0' : '#718096' }}>
                        {' '}{result.context.after}...
                      </span>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Sidebar - Annotations & Highlights */}
        {showSidebar && (
          <div style={{ 
            width: sideWidth('300px'), ...sideOverlay,
            background: isDarkMode ? '#0f0f1a' : 'white',
            borderRight: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`,
            overflow: 'auto',
            padding: '1rem',
            flexShrink: 0
          }}>
            <h3 style={{ fontWeight: 600, marginBottom: '1rem', color: isDarkMode ? 'white' : '#2d3748', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Minhas Anotações
              {(
                <button onClick={() => setShowSidebar(false)} data-testid="annotations-close-btn" title="Fechar"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDarkMode ? '#cbd5e0' : '#718096', padding: '0.25rem' }}>
                  <X className="w-5 h-5" />
                </button>
              )}
            </h3>
            
            {/* Notes Section */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ fontSize: '0.85rem', color: isDarkMode ? '#cbd5e0' : '#718096', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <StickyNote className="w-4 h-4" /> Notas ({annotations.length})
              </h4>
              {annotations.length === 0 ? (
                <p style={{ color: '#a0aec0', fontSize: '0.85rem' }}>Nenhuma nota ainda</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {annotations.map((ann) => (
                    <div 
                      key={ann.id}
                      id={`annotation-${ann.id}`}
                      style={{ 
                        padding: '0.75rem',
                        borderRadius: '8px',
                        background: focusedAnnotationId === ann.id 
                          ? (isDarkMode ? '#2d3748' : '#e2e8f0') 
                          : (isDarkMode ? '#1a1a2e' : '#f7fafc'),
                        borderLeft: `4px solid ${ann.color}`,
                        cursor: 'pointer',
                        transition: 'background 0.3s ease',
                        boxShadow: focusedAnnotationId === ann.id ? '0 0 0 2px #68AAB0' : 'none'
                      }}
                      onClick={() => {
                        goToPage(ann.page);
                        setFocusedAnnotationId(ann.id);
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span style={{ fontSize: '0.75rem', color: isDarkMode ? '#cbd5e0' : '#718096' }}>Pág. {ann.page}</span>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleDeleteAnnotation(ann.id); }}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <p style={{ fontSize: '0.85rem', color: isDarkMode ? '#e2e8f0' : '#2d3748', margin: 0 }}>
                        {ann.note}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Highlights Section */}
            <div>
              <h4 style={{ fontSize: '0.85rem', color: isDarkMode ? '#cbd5e0' : '#718096', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Highlighter className="w-4 h-4" /> Grifos ({highlights.length})
              </h4>
              {highlights.length === 0 ? (
                <p style={{ color: '#a0aec0', fontSize: '0.85rem' }}>Nenhum grifo ainda</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {highlights.map((hl) => (
                    <div 
                      key={hl.id}
                      style={{ 
                        padding: '0.75rem',
                        borderRadius: '8px',
                        background: `${hl.color}30`,
                        borderLeft: `4px solid ${hl.color}`,
                        cursor: 'pointer'
                      }}
                      onClick={() => goToPage(hl.page)}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                        <span style={{ fontSize: '0.75rem', color: isDarkMode ? '#cbd5e0' : '#718096' }}>Pág. {hl.page}</span>
                        <button 
                          onClick={(e) => { e.stopPropagation(); handleDeleteAnnotation(hl.id, true); }}
                          style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                      <p style={{ 
                        fontSize: '0.85rem', 
                        color: isDarkMode ? '#e2e8f0' : '#2d3748', 
                        margin: 0,
                        fontStyle: 'italic'
                      }}>
                        "{hl.text}"
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Book Display */}
        <div 
          ref={pagesContainerRef}
          style={{ 
            flex: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            padding: isFullscreen ? '0.5rem' : '1rem',
            minHeight: 0
          }}
        >
          {/* Left Navigation Arrow */}
          <button
            onClick={goToPrevious}
            disabled={currentPage === 1}
            style={{
              position: 'fixed',
              left: isCompact ? '0.25rem' : `calc(${(showSidebar ? 304 : 0) + (showTTSControls ? 280 : 0) + (showSearchSidebar ? 350 : 0)}px + 1rem)`,
              top: '50%',
              transform: 'translateY(-50%)',
              width: isCompact ? '30px' : '50px',
              height: isCompact ? '56px' : '80px',
              opacity: isCompact ? 0.75 : 1,
              background: currentPage === 1 ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.3)',
              border: 'none',
              borderRadius: '8px',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              transition: 'all 0.2s ease'
            }}
            data-testid="prev-btn"
          >
            <ChevronLeft className="w-8 h-8" style={{ color: 'white' }} />
          </button>

          {/* Book Pages Container - with scroll for zoom */}
          <div ref={pageScrollRef} data-testid="reader-page-scroll" style={{ 
            display: 'flex',
            flexDirection: 'row',
            gap: '4px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
            background: isDarkMode ? '#2d2d44' : '#f5f5f5',
            borderRadius: '4px',
            maxWidth: '100%',
            maxHeight: '100%',
            overflow: scale > 1 ? 'auto' : 'hidden'
          }}>
            {pdfUrl && (
              <Document
                key={bookId}
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={handlePdfLoadError}
                options={pdfOptions}
                loading={PdfLoadingComponent}
                error={PdfErrorComponent}
                className="pdf-document-spread"
              >
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  {visiblePages.map((pageNum, index) => {
                    const pageAnnotations = getPageAnnotations(pageNum);
                    const hasAnnotations = pageAnnotations.length > 0;
                    
                    return (
                      <div 
                        key={pageNum}
                        style={{ 
                          background: 'white',
                          boxShadow: viewMode === 'double' 
                            ? (index === 0 ? 'inset -10px 0 20px rgba(0,0,0,0.1)' : 'inset 10px 0 20px rgba(0,0,0,0.1)')
                            : 'none',
                          position: 'relative'
                        }}
                      >
                        {/* Annotation Tab - shown when page has notes */}
                        {hasAnnotations && (
                          <button
                            onClick={() => handleAnnotationTabClick(pageNum)}
                            style={{
                              position: 'absolute',
                              top: '12px',
                              right: '-2px',
                              width: '28px',
                              height: '40px',
                              background: 'linear-gradient(135deg, #68AAB0 0%, #5a9a9f 100%)',
                              border: 'none',
                              borderRadius: '4px 0 0 4px',
                              cursor: 'pointer',
                              zIndex: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '-2px 2px 6px rgba(0,0,0,0.2)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.width = '32px';
                              e.currentTarget.style.background = 'linear-gradient(135deg, #7BBBC1 0%, #68AAB0 100%)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.width = '28px';
                              e.currentTarget.style.background = 'linear-gradient(135deg, #68AAB0 0%, #5a9a9f 100%)';
                            }}
                            title={`${pageAnnotations.length} anotação(ões) nesta página`}
                            data-testid={`annotation-tab-page-${pageNum}`}
                          >
                            <div style={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              color: 'white'
                            }}>
                              <FileText className="w-4 h-4" />
                              {pageAnnotations.length > 1 && (
                                <span style={{ 
                                  fontSize: '0.6rem', 
                                  fontWeight: 'bold',
                                  marginTop: '2px'
                                }}>
                                  {pageAnnotations.length}
                                </span>
                              )}
                            </div>
                          </button>
                        )}
                        
                        <Page
                          pageNumber={pageNum}
                          scale={scale * fitScale}
                          onRenderTextLayerSuccess={onTextLayerRendered}
                          renderTextLayer={true}
                          renderAnnotationLayer={true}
                          className="pdf-page"
                        />
                        
                        {/* Dynamic Watermark - Distributed name and CPF separately */}
                        {subscriberInfo && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              pointerEvents: 'none',
                              zIndex: 5,
                              overflow: 'visible'
                            }}
                            data-testid="watermark-overlay"
                          >
                            {/* NAME watermarks - grid pattern across entire page */}
                            {[
                              { top: '3%', left: '2%' },
                              { top: '3%', left: '50%' },
                              { top: '18%', left: '25%' },
                              { top: '18%', left: '70%' },
                              { top: '33%', left: '5%' },
                              { top: '33%', left: '55%' },
                              { top: '48%', left: '20%' },
                              { top: '48%', left: '65%' },
                              { top: '63%', left: '3%' },
                              { top: '63%', left: '45%' },
                              { top: '78%', left: '25%' },
                              { top: '78%', left: '70%' },
                              { top: '93%', left: '8%' },
                              { top: '93%', left: '55%' }
                            ].map((pos, idx) => (
                              <div
                                key={`name-${idx}`}
                                style={{
                                  position: 'absolute',
                                  top: pos.top,
                                  left: pos.left,
                                  transform: 'rotate(-35deg)',
                                  transformOrigin: 'left top',
                                  color: 'rgba(180, 30, 30, 0.18)',
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                  userSelect: 'none',
                                  letterSpacing: '1px'
                                }}
                              >
                                {subscriberInfo.name || subscriberInfo.email}
                              </div>
                            ))}
                            
                            {/* CPF watermarks - grid pattern across entire page */}
                            {[
                              { top: '8%', left: '15%' },
                              { top: '8%', left: '60%' },
                              { top: '23%', left: '5%' },
                              { top: '23%', left: '45%' },
                              { top: '38%', left: '30%' },
                              { top: '38%', left: '75%' },
                              { top: '53%', left: '10%' },
                              { top: '53%', left: '50%' },
                              { top: '68%', left: '25%' },
                              { top: '68%', left: '65%' },
                              { top: '83%', left: '5%' },
                              { top: '83%', left: '50%' },
                              { top: '98%', left: '20%' },
                              { top: '98%', left: '65%' }
                            ].map((pos, idx) => (
                              <div
                                key={`cpf-${idx}`}
                                style={{
                                  position: 'absolute',
                                  top: pos.top,
                                  left: pos.left,
                                  transform: 'rotate(-35deg)',
                                  transformOrigin: 'left top',
                                  color: 'rgba(180, 30, 30, 0.18)',
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                  userSelect: 'none',
                                  letterSpacing: '1px'
                                }}
                              >
                                CPF: {subscriberInfo.cpf || 'N/A'}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Document>
            )}
          </div>

          {/* Right Navigation Arrow */}
          <button
            onClick={goToNext}
            disabled={viewMode === 'single' ? currentPage >= numPages : currentPage + 1 >= numPages}
            style={{
              position: 'fixed',
              right: isCompact ? (showAIChat && window.innerWidth > 768 ? 'calc(380px + 0.25rem)' : '0.25rem') : (showAIChat ? 'calc(380px + 1rem)' : '1rem'),
              top: '50%',
              transform: 'translateY(-50%)',
              width: isCompact ? '30px' : '50px',
              height: isCompact ? '56px' : '80px',
              opacity: isCompact ? 0.75 : 1,
              background: (viewMode === 'single' ? currentPage >= numPages : currentPage + 1 >= numPages) 
                ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.3)',
              border: 'none',
              borderRadius: '8px',
              cursor: (viewMode === 'single' ? currentPage >= numPages : currentPage + 1 >= numPages) 
                ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              transition: 'all 0.2s ease'
            }}
            data-testid="next-btn"
          >
            <ChevronRight className="w-8 h-8" style={{ color: 'white' }} />
          </button>

          {/* Highlight Popup - Simplified (no comment) */}
          {showHighlightPopup && selectionPosition && (
            <div ref={highlightPopupRef} data-testid="highlight-popup" style={{
              position: 'fixed',
              left: popupPos ? popupPos.left : selectionPosition.vx,
              top: popupPos ? popupPos.top : selectionPosition.vTop,
              visibility: popupPos ? 'visible' : 'hidden',
              background: isDarkMode ? '#1a1a2e' : 'white',
              color: isDarkMode ? '#f7fafc' : undefined,
              border: isDarkMode ? '1px solid #2d3748' : 'none',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              padding: '0.75rem',
              zIndex: 5000,
              minWidth: '200px'
            }}>
              <p style={{ 
                fontSize: '0.85rem', 
                fontStyle: 'italic', 
                background: `${selectedColor}50`,
                padding: '0.5rem',
                borderRadius: '4px',
                marginBottom: '0.5rem',
                maxWidth: '250px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                "{selectedText.substring(0, 50)}{selectedText.length > 50 ? '...' : ''}"
              </p>
              
              {/* Color picker */}
              <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.5rem', justifyContent: 'center' }}>
                {HIGHLIGHT_COLORS.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setSelectedColor(color.value)}
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: color.value,
                      border: selectedColor === color.value ? `2px solid ${isDarkMode ? '#ffffff' : '#2d3748'}` : '1px solid #ccc',
                      cursor: 'pointer'
                    }}
                    title={color.name}
                  />
                ))}
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                <Button 
                  variant="outline" 
                  size="sm"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    window.getSelection()?.removeAllRanges();
                    setShowHighlightPopup(false);
                    setSelectedText('');
                  }}
                  style={isDarkMode ? { background: 'transparent', color: '#f7fafc', borderColor: '#4a5568' } : undefined}
                >
                  Cancelar
                </Button>
                <Button 
                  variant="outline"
                  size="sm"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setCiteText(selectedText);
                    setCitePage(selectionPosition?.pageNum || currentPage);
                    setShowCiteModal(true);
                    setShowHighlightPopup(false);
                    window.getSelection()?.removeAllRanges();
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', ...(isDarkMode ? { background: 'transparent', color: '#f7fafc', borderColor: '#4a5568' } : {}) }}
                  data-testid="cite-btn"
                >
                  <Quote className="w-3 h-3" />
                  Citar
                </Button>
                <Button 
                  size="sm"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={handleSaveHighlight}
                  style={{ background: selectedColor, color: '#000' }}
                  data-testid="highlight-btn"
                >
                  <Highlighter className="w-3 h-3" style={{ marginRight: '0.25rem' }} />
                  Grifar
                </Button>
              </div>
            </div>
          )}
        </div>
        {showAIChat && aiEnabled && (
          <BookAIChat bookId={bookId} token={token} isDarkMode={isDarkMode} onClose={() => setShowAIChat(false)} />
        )}
      </div>

      {/* Bottom Navigation Bar with Footer */}
      <div data-testid="reader-bottom-bar" style={{ 
        padding: isCompact ? '0.5rem 0.75rem' : '0.75rem 1rem',
        paddingBottom: isCompact ? 'max(0.5rem, env(safe-area-inset-bottom))' : '0.75rem',
        background: isDarkMode ? '#0f0f1a' : '#1a1f2e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: isCompact ? '0.75rem' : '1rem',
        flexShrink: 0
      }}>
        {/* Editor Name - Left */}
        <span style={{ 
          display: isCompact ? 'none' : 'inline',
          color: 'rgba(255,255,255,0.7)', 
          fontSize: '0.8rem',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          minWidth: '120px'
        }}>
          {editorName || 'Biblioteca Digital'}
        </span>
        
        {/* Navigation Controls - Center */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: '1rem',
          flex: 1
        }}>
          {/* Page Slider */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            maxWidth: '400px',
            flex: 1,
            position: 'relative'
          }}>
            <span style={{ color: 'white', fontSize: '0.85rem' }}>1</span>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                ref={sliderRef}
                type="range"
                min="1"
                max={numPages}
                value={showSliderTooltip ? sliderValue : currentPage}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  setSliderValue(value);
                  if (!showSliderTooltip) {
                    goToPage(value);
                  }
                }}
                onInput={(e) => {
                  const value = parseInt(e.target.value);
                  setSliderValue(value);
                }}
                onMouseDown={(e) => {
                  const value = parseInt(e.target.value);
                  setSliderValue(value);
                  setShowSliderTooltip(true);
                }}
                onMouseUp={(e) => {
                  const value = parseInt(e.target.value);
                  goToPage(value);
                  setTimeout(() => setShowSliderTooltip(false), 100);
                }}
                onTouchStart={(e) => {
                  setSliderValue(currentPage);
                  setShowSliderTooltip(true);
                }}
                onTouchEnd={(e) => {
                  goToPage(sliderValue);
                  setTimeout(() => setShowSliderTooltip(false), 100);
                }}
                style={{ 
                  width: '100%', 
                  cursor: 'pointer',
                  accentColor: '#68AAB0'
                }}
              />
              {/* Tooltip showing page number while dragging */}
              {showSliderTooltip && (
                <div 
                  style={{
                    position: 'absolute',
                    bottom: '30px',
                    left: `calc(${((sliderValue - 1) / Math.max(numPages - 1, 1)) * 100}%)`,
                    transform: 'translateX(-50%)',
                    background: '#68AAB0',
                    color: 'white',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    pointerEvents: 'none',
                    zIndex: 100
                  }}
                >
                  Pág. {sliderValue}
                  {/* Arrow pointing down */}
                  <div style={{
                    position: 'absolute',
                    bottom: '-6px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid #68AAB0'
                  }} />
                </div>
              )}
            </div>
            <span style={{ color: 'white', fontSize: '0.85rem' }}>{numPages}</span>
          </div>
          
          {/* Quick Page Jump */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ color: 'white', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{isCompact ? 'Pág.' : 'Ir para:'}</span>
            <input
              type="text"
              data-testid="reader-page-input"
              inputMode="numeric"
              pattern="[0-9]*"
              value={currentPage}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                if (value === '') return;
                const pageNum = parseInt(value);
                if (pageNum >= 1 && pageNum <= numPages) {
                  goToPage(pageNum);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
              style={{ 
                width: '50px',
                padding: '0.35rem 0.5rem',
                borderRadius: '6px',
                border: '1px solid #4a5568',
                background: 'transparent',
                color: 'white',
                fontSize: '0.9rem',
                textAlign: 'center',
                outline: 'none',
                MozAppearance: 'textfield',
                WebkitAppearance: 'none'
              }}
            />
          </div>
        </div>
        
        {/* Livrum Logo - Right */}
        <a href="/" style={{ display: isCompact ? 'none' : 'flex', alignItems: 'center', minWidth: '120px', justifyContent: 'flex-end' }}>
          <img 
            src="/livrum-logo-light.webp" 
            alt="Livrum" 
            style={{ height: '26px', width: 'auto', opacity: 0.7, filter: 'brightness(0) invert(1)' }}
            data-testid="reader-footer-logo"
          />
        </a>
      </div>

      {/* Note Modal */}
      {showNoteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div data-testid="note-modal" style={{
            background: isDarkMode ? '#1a1a2e' : 'white',
            color: isDarkMode ? '#f7fafc' : undefined,
            border: isDarkMode ? '1px solid #2d3748' : 'none',
            borderRadius: '12px',
            padding: '1.5rem',
            width: '90%',
            maxWidth: '400px'
          }}>
            <h3 style={{ marginBottom: '1rem', fontWeight: 600 }}>Adicionar Nota</h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.85rem', color: isDarkMode ? '#cbd5e0' : '#718096', display: 'block', marginBottom: '0.5rem' }}>
                Página: {notePage}
              </label>
              <Input
                type="number"
                min={1}
                max={numPages}
                value={notePage}
                onChange={(e) => setNotePage(parseInt(e.target.value) || 1)}
                style={isDarkMode ? { background: '#0f0f1a', color: '#ffffff', borderColor: '#4a5568' } : undefined}
                data-testid="note-page-input"
              />
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.85rem', color: isDarkMode ? '#cbd5e0' : '#718096', display: 'block', marginBottom: '0.5rem' }}>
                Sua nota
              </label>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Digite sua anotação..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                  background: isDarkMode ? '#0f0f1a' : 'white',
                  color: isDarkMode ? '#ffffff' : undefined,
                  resize: 'vertical'
                }}
                data-testid="note-text-input"
              />
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <Button variant="outline" onClick={() => setShowNoteModal(false)} style={isDarkMode ? { background: 'transparent', color: '#f7fafc', borderColor: '#4a5568' } : undefined}>
                Cancelar
              </Button>
              <Button onClick={handleAddNote} style={{ background: '#68AAB0' }}>
                Salvar Nota
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Content Protection Overlay - dims content to 5% visibility */}
      {contentProtected && (
        <div 
          data-testid="content-protection-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.95)', // 95% black = 5% content visibility
            zIndex: 9998,
            pointerEvents: 'none'
          }}
        />
      )}

      {/* Citation Modal */}
      {showCiteModal && (
        <div 
          data-testid="cite-modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9998
          }}
          onClick={() => setShowCiteModal(false)}
        >
          <div 
            style={{
              background: isDarkMode ? '#1a1a2e' : 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '45px',
                  height: '45px',
                  background: 'linear-gradient(135deg, #68AAB0 0%, #5a9a9f 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Quote className="w-6 h-6" style={{ color: 'white' }} />
                </div>
                <h2 style={{ 
                  fontSize: '1.3rem', 
                  fontWeight: 700, 
                  color: isDarkMode ? '#e2e8f0' : '#1a202c',
                  margin: 0
                }}>
                  Citação
                </h2>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowCiteModal(false)}
                style={{ color: isDarkMode ? '#a0aec0' : '#718096' }}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Citation Text */}
            <div style={{
              background: isDarkMode ? '#0f0f1a' : '#f8fafc',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              border: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`
            }}>
              <p style={{ 
                color: isDarkMode ? '#e2e8f0' : '#2d3748',
                fontSize: '1rem',
                lineHeight: '1.8',
                fontStyle: 'italic',
                margin: 0,
                whiteSpace: 'pre-wrap'
              }}>
                "{citeText}"
              </p>
            </div>
            
            {/* Source Info */}
            <div style={{
              background: isDarkMode ? '#0f0f1a' : '#f1f5f9',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              color: isDarkMode ? '#a0aec0' : '#64748b'
            }}>
              <p style={{ margin: '0 0 0.5rem 0' }}>
                <strong>Fonte:</strong> {book?.titulo || 'Livro Digital'}
              </p>
              {book?.autor && (
                <p style={{ margin: '0 0 0.5rem 0' }} data-testid="cite-author">
                  <strong>Autor:</strong> {book.autor}
                </p>
              )}
              {book?.isbn && (
                <p style={{ margin: '0 0 0.5rem 0' }} data-testid="cite-identifier">
                  <strong>{isIssn(book.isbn) ? 'ISSN' : 'ISBN'}:</strong> {String(book.isbn).trim()}
                </p>
              )}
              <p style={{ margin: 0 }}>
                <strong>Página:</strong> {citePage} de {numPages}
              </p>
            </div>
            
            {/* Copy Button */}
            <CopyButton text={citeText} book={book} page={citePage} isDarkMode={isDarkMode} />
          </div>
        </div>
      )}

      {/* Copyright Protection Modal */}
      {showCopyrightModal && (
        <div 
          data-testid="copyright-modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div style={{
            background: isDarkMode ? '#1a1a2e' : 'white',
            border: isDarkMode ? '1px solid #2d3748' : 'none',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '450px',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'fadeIn 0.3s ease-out'
          }}>
            {/* Warning Icon */}
            <div style={{
              width: '70px',
              height: '70px',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <AlertCircle className="w-10 h-10" style={{ color: 'white' }} />
            </div>
            
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: 700, 
              color: isDarkMode ? '#f7fafc' : '#1a202c',
              marginBottom: '1rem'
            }}>
              Ação Não Permitida
            </h2>
            
            <p style={{ 
              color: isDarkMode ? '#e2e8f0' : '#4a5568', 
              fontSize: '1rem',
              lineHeight: '1.6',
              marginBottom: '1.5rem'
            }}>
              A captura de tela, cópia ou impressão deste conteúdo é 
              <strong style={{ color: '#dc2626' }}> estritamente proibida</strong> conforme a 
              <strong> Lei nº 9.610/98 (Lei de Direitos Autorais)</strong>.
            </p>
            
            <p style={{ 
              color: isDarkMode ? '#cbd5e0' : '#718096', 
              fontSize: '0.9rem',
              marginBottom: '1.5rem',
              padding: '1rem',
              background: isDarkMode ? '#0f0f1a' : '#f7fafc',
              borderRadius: '8px',
              border: `1px solid ${isDarkMode ? '#2d3748' : '#e2e8f0'}`
            }}>
              O conteúdo digital é protegido por direitos autorais. 
              A reprodução não autorizada constitui crime previsto em lei, 
              sujeito a penalidades civis e criminais.
            </p>
            
            <Button 
              onClick={() => {
                setShowCopyrightModal(false);
                restoreContent();
              }}
              style={{ 
                background: 'linear-gradient(135deg, #68AAB0 0%, #5a9a9f 100%)',
                color: 'white',
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: '8px',
                width: '100%'
              }}
              data-testid="copyright-modal-close"
            >
              Entendi
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookViewer;
